/**
 * Subscription page — the in-app sales floor (2026-08-14 full revamp,
 * owner directive: "very personal to the user's current account · expert
 * salesperson · sleek and modern · sets the right expectations").
 *
 * The page reads the account's actual posture and renders the right
 * conversation, in the order a great salesperson would run it — value
 * before price, price in one sentence, expectations in plain language:
 *
 *  1. TRIAL, quiet  — under $25 of PRO Work Units used: no prices at all.
 *     Personal trial status only; offers unlock as real work flows.
 *  2. TRIAL, unlocked / NO PLAN — the full presentation: what your trial
 *     already produced (value first), then ONE recommended offer — annual,
 *     at the published minimum for the organization's size on file
 *     (20+ people → 3 Prest0ns · 40+ → 5) — with a live explorer: term,
 *     seats, and the published ladder, every number updating as you click.
 *  3. ACTIVE — the personal plan panel (seats, term, renewal date, what it
 *     covers), the upgrade path (monthly→annual conversion, add-Prest0ns
 *     with automatic team rates), and honest reduce/renewal expectations
 *     straight from the published pricing sheet.
 *
 * Everything renews automatically; the page says so plainly (rate locked
 * for the term, change team size in one click before renewal, nothing
 * beyond the committed team ever bills without admin authorization).
 *
 * Sales language sourced from the marketing pack (Pricing — Public, Master
 * Chart, Sales Script): commitment is the discount; the ladder is
 * published and identical for everyone; minimums are deployment
 * standards — "we don't deploy under-staffed"; discount in capacity,
 * never in price. Deal-desk material never renders here.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import SeoHead from "../components/SeoHead";
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { shouldTagAsTest, setRuntimeTestingEnvironment } from '../config/agenticWorkflowConfig';
import {
  OrgSizeBand,
  ORG_SIZE_BANDS,
  recommendPlan,
  shouldRevealPlanOffers,
  TRIAL_PRICING_REVEAL_USD,
} from '../config/planRecommendation';
import {
  WU_PRICING,
  MONTHLY_WU_PER_PRESTON,
  WU_FULL_NAME,
  WU_LABEL,
  STANDARD_RATE_USD_PER_WU,
  usdToWu,
  formatWu,
  hireQuote,
  HireCadence,
} from '../config/workUnits';
import { WorkCalculatorLauncher } from '../components/pricing/WorkUnitCalculator';
import Pr0Unit from '../components/common/Pr0Unit';
import GlobalFooter from '../components/GlobalFooter';
import KeepCase from '../components/common/KeepCase';
import Modal from '../components/common/Modal';
// Stripe.js loads lazily — the promise is only created when the payment
// modal first opens (sharedStripe), so this import costs nothing up front.
// Task #73 (2026-08-16 owner directive): hiring pays like a TOP-UP — the
// saved card preselected, one confirm, our branded modal. Stripe's embedded
// checkout (the Link-first, many-field surface) is gone; Elements powers a
// CardElement only when the buyer chooses "use a new card".
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getSharedStripePromise } from '../components/wallet/sharedStripe';
import { stripeApi } from '../services/stripeClient';
import {
  listOrgSharedPaymentMethods,
  SharedPaymentMethod,
} from '../services/paymentMethodSharing';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import {
  invalidateBillingSnapshot,
  readCachedBillingSnapshot,
} from '../services/billingSnapshotCache';
import { useMaybeOLVRate } from '../context/OLVRateContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
// Workspace-scoped trial derivation (2026-08-17): the §6 'view' surface keys
// the workspace's trial posture off the same pure rules the global banner
// uses, fed by getWorkspaceSubscriptionStatus's facts payload.
import { deriveWorkspaceTrialStatus, WorkspaceTrialFacts } from '../hooks/trialStatus';

// ============================================================================
// Types
// ============================================================================

interface SubscriptionInfo {
  status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing' | string;
  plan: string;
  /** Per-seat amount at the plan's own interval (annual tiers store the yearly figure). */
  planAmountUsd: number;
  planInterval?: 'month' | 'year' | string;
  planSeats?: number;
  planWuPerSeatMonthly?: number | null;
  /** 12 on committed hires (annual billed monthly, prepaid year); null on monthly. */
  planCommitmentMonths?: number | null;
  currentPeriodEnd: number | null;
  aiEnabled: boolean;
  graceExpiresAt: number | null;
  isTest?: boolean;
  /** Trial-account flag (redeemAccessCode / organic-trial subscriptions). */
  is_trial?: boolean;
  /** Lifetime wallet spend in OLV units — convert with the live rate for USD. */
  totalSpent?: number | null;
}

/** getWorkspaceSubscriptionStatus payload → the page's SubscriptionInfo shape
 * (task #80 org-manage mode: same facts the owner path reads). */
function wsSubToInfo(orgSub: any): SubscriptionInfo {
  return {
    status: String(orgSub?.status ?? ''),
    plan: String(orgSub?.plan ?? ''),
    planAmountUsd: typeof orgSub?.planAmountUsd === 'number' ? orgSub.planAmountUsd : 0,
    planInterval: orgSub?.planInterval ?? 'month',
    planSeats: typeof orgSub?.seatCount === 'number' ? orgSub.seatCount : undefined,
    planWuPerSeatMonthly: orgSub?.planWuPerSeatMonthly ?? null,
    planCommitmentMonths: orgSub?.planCommitmentMonths ?? null,
    currentPeriodEnd: typeof orgSub?.currentPeriodEnd === 'number' ? orgSub.currentPeriodEnd : null,
    aiEnabled: orgSub?.aiEnabled === true,
    graceExpiresAt: typeof orgSub?.graceExpiresAt === 'number' ? orgSub.graceExpiresAt : null,
    isTest: orgSub?.isTest === true,
    is_trial: orgSub?.isTrial === true,
  };
}

const PLAN_DISPLAY: Record<string, { name: string; tagline: string }> = {
  // Legacy tiers — valid for existing subscribers' display, no longer offered.
  starter: { name: 'starter', tagline: 'core ai tools — shared compute' },
  professional: { name: 'professional', tagline: 'priority compute, Slack integration, advanced workflows' },
  enterprise: { name: 'enterprise', tagline: 'dedicated VM, SLA, custom integrations' },
  // The 2026-08 PRO Work Units ladder (lowercase — house style, 2026-08-16).
  wu_monthly: {
    name: 'Prest0n professional — monthly',
    tagline: 'full-time digital employee — 2,200 pr0 per month, cancel anytime',
  },
  wu_annual: {
    name: 'Prest0n professional — annual',
    tagline: 'full-time digital employee — annual hire, 2,200 pr0 per month',
  },
  wu_annual_team3: {
    name: 'Prest0n team — annual · 3+',
    tagline: 'team of full-time digital employees — 2,200 pr0 each per month',
  },
  wu_annual_team5: {
    name: 'Prest0n team — annual · 5+',
    tagline: 'team of full-time digital employees — 2,200 pr0 each per month, best rate',
  },
  // Prepaid-year siblings (2026-08-16 owner directive): 20% off the seat-count
  // rate, one payment — a discount, never bonus capacity.
  wu_prepay: {
    name: 'Prest0n professional — prepaid year',
    tagline: '20% off the annual rate — one payment, 2,200 pr0 per month',
  },
  wu_prepay_team3: {
    name: 'Prest0n team — prepaid year · 3+',
    tagline: '20% off the team rate — one payment, 2,200 pr0 each per month',
  },
  wu_prepay_team5: {
    name: 'Prest0n team — prepaid year · 5+',
    tagline: '20% off the best rate — one payment, 2,200 pr0 each per month',
  },
};

// ── The hire calculator — shared with /pricing (src/config/workUnits.ts) ────
// 2026-08-16 owner directive: tiers never render as a table; the cadence
// toggle + seat count recompute the rate and % discount live. 'prepay' is
// the upfront cadence: 20% OFF the seat-count rate, one payment for the
// year — a discount, never bonus capacity.

type OfferTerm = HireCadence;

/** Rank of each annual rung — used for "already on recommended-or-higher". */
const ANNUAL_TIER_RANK: Record<string, number> = {
  wu_annual: 1,
  wu_annual_team3: 2,
  wu_annual_team5: 3,
};

// Task #70 (owner): never cap how many Prest0ns a team can hire from the UI.
// The server sanity-clamps at 500; large teams type the number directly.
const MAX_SEATS = 500;

/** The one price sentence for the current selection. */
function selectionPricing(term: OfferTerm, seats: number) {
  const q = hireQuote(term, seats);
  return {
    tier: q.tier as string,
    monthlyUsdPerSeat: q.perSeatMonthlyUsd,
    usdPerWu: q.usdPerWu,
    savePct: q.savePct,
    wuPerSeat: q.wuPerSeat,
    upfrontTotalUsd: q.upfrontTotalUsd,
  };
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * How long the personal subscription read waits for the workspace resolution
 * before giving up on it. Long enough for a warm callable, short enough that
 * a wedged one costs a beat rather than the page.
 */
const SCOPE_FALLBACK_MS = 2500;

function getProjectId(): string {
  return process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
}

function fnUrl(name: string): string {
  return `https://us-central1-${getProjectId()}.cloudfunctions.net/${name}`;
}

/**
 * Auth can still be rehydrating when this page mounts — a fresh sign-in or an
 * incognito cold load resolves the AuthContext user before
 * `getAuth().currentUser` is populated. The old single null read threw
 * "Not signed in", which surfaced as a permanent red "Failed to load
 * subscription info" on a perfectly healthy trial account (owner report
 * 2026-08-15). Wait briefly for the real user instead of failing the call.
 */
async function getAuthToken(): Promise<string | null> {
  const auth = getAuth();
  let user = auth.currentUser;
  if (!user) {
    user = await new Promise<ReturnType<typeof getAuth>['currentUser']>((resolve) => {
      let settled = false;
      const finish = (u: ReturnType<typeof getAuth>['currentUser']) => {
        if (settled) return;
        settled = true;
        try { unsub(); } catch { /* already torn down */ }
        clearTimeout(timer);
        resolve(u);
      };
      const unsub = onAuthStateChanged(auth, (u) => { if (u) finish(u); });
      const timer = setTimeout(() => finish(auth.currentUser), AUTH_WAIT_MS);
    });
  }
  if (!user) return null;
  return user.getIdToken();
}

/** How long to wait for auth to rehydrate before giving up on the token. */
const AUTH_WAIT_MS = 4000;

function formatDate(unix: number | null): string {
  if (!unix) return '—';
  return new Date(unix * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.main`
  padding: 1.5rem 1rem;
  width: 100%;
  margin: 0 auto;
  min-height: 100vh;
  background: var(--brand-canvas);

  @media (min-width: 768px) {
    padding: 2rem 1.5rem;
  }

  @media (min-width: 1024px) {
    max-width: 900px;
    padding: 2.5rem;
    min-height: calc(100vh - 160px);
  }

  @media (min-width: 1200px) {
    max-width: 1000px;
  }
`;

const PageTitle = styled.h1`
  font-family: 'Space Grotesk', 'Inter', -apple-system, sans-serif;
  font-size: 2rem;
  color: var(--brand-text);
  margin-bottom: 1.5rem;
  font-weight: 400;
  letter-spacing: -0.02em;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 2rem;
  }
`;

const Section = styled.section`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.375rem 1.25rem rgba(0, 0, 0, 0.04);
  border: 1px solid var(--brand-line);

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.08em;
  color: #AAA;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const PlanName = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--brand-text);
  letter-spacing: -0.02em;
  margin-bottom: 0.25rem;
`;

const PlanPrice = styled.div`
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 1.25rem;
`;

interface StatusBadgeProps {
  variant: 'active' | 'past_due' | 'canceled' | 'inactive' | 'trialing';
}

const STATUS_COLORS: Record<StatusBadgeProps['variant'], { bg: string; color: string }> = {
  active:   { bg: '#F0FFF4', color: '#27AE60' },
  trialing: { bg: '#EDF7FF', color: '#2980B9' },
  past_due: { bg: '#FFF9E6', color: '#D68910' },
  canceled: { bg: '#FFF0ED', color: '#C0392B' },
  inactive: { bg: '#F5F5F5', color: '#888' },
};

const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: ${(p) => STATUS_COLORS[p.variant as StatusBadgeProps['variant']].bg};
  color: ${(p) => STATUS_COLORS[p.variant as StatusBadgeProps['variant']].color};
`;

const StatusDot = styled.span<{ variant: StatusBadgeProps['variant'] }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  display: inline-block;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-top: 1px solid #F0F0F0;
  font-size: 0.875rem;
  color: #444;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const DetailLabel = styled.span`
  color: #888;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: var(--brand-text);
  color: white;
  border: none;
  border-radius: 0.625rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
  margin-top: 1.25rem;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: none;
  color: #555;
  border: 1px solid #D4D4D0;

  &:hover:not(:disabled) {
    background: var(--brand-surface);
    color: var(--brand-text);
  }
`;

const GraceNotice = styled.div`
  background: #FFF9E6;
  border: 1px solid #F5CBA7;
  border-radius: 0.625rem;
  padding: 0.875rem 1rem;
  font-size: 0.85rem;
  color: #784212;
  margin-top: 1rem;
  line-height: 1.5;
`;

const ErrorText = styled.div`
  color: #C0392B;
  font-size: 0.875rem;
  margin-top: 0.75rem;
`;

const LoadingText = styled.div`
  color: #AAA;
  font-size: 0.875rem;
  padding: 1rem 0;
`;

const ValueNotice = styled.div`
  margin-top: 1.25rem;
  background: linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%);
  border: 1px solid rgba(200, 167, 94, 0.28);
  border-radius: 0.875rem;
  padding: 1rem 1rem 0.95rem;
`;

const ValueTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 0.4rem;
`;

const ValueText = styled.div`
  font-size: 0.84rem;
  line-height: 1.6;
  color: #555;
`;

/** Clickable header for the collapsed commitment FAQ (2026-08-14 copy diet:
 *  the five answers live one click away instead of a page-length prose wall). */
const FaqSummary = styled.summary`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.08em;
  color: #8A6F2F;
  font-weight: 600;
  cursor: pointer;
  list-style: none;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  &::before {
    content: '▸';
    transition: transform 0.15s ease;
  }
  details[open] > &::before {
    transform: rotate(90deg);
  }
  details[open] > & {
    margin-bottom: 1rem;
  }
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: 0.25rem;
`;

const FAQItem = styled.div`
  border-top: 1px solid #F0F0F0;
  padding-top: 0.85rem;

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const FAQQuestion = styled.div`
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 0.25rem;
`;

const FAQAnswer = styled.div`
  font-size: 0.84rem;
  line-height: 1.6;
  color: #666;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const SetupOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  text-align: center;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid var(--brand-line);
  border-top-color: var(--brand-text);
  border-radius: 50%;
  animation: ${spin} 0.75s linear infinite;
`;

const SetupTitle = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--brand-text);
`;

const SetupSub = styled.div`
  font-size: 0.85rem;
  color: #888;
`;

const TestBadge = styled.span`
  display: inline-block;
  background: #FFF3CD;
  color: #856404;
  border: 1px solid #FFE69C;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: lowercase;
  letter-spacing: 0.05em;
  padding: 2px 8px;
  border-radius: 4px;
`;

// ── The offer card (gold centerpiece) ──

const RecCard = styled.section`
  position: relative;
  background: linear-gradient(150deg, #FFFDF7 0%, #FAF4E4 60%, #F5ECD3 100%);
  border: 1px solid rgba(200, 167, 94, 0.5);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.5rem 1.75rem rgba(176, 141, 61, 0.14);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--brand-gold-alt) 0%, #EAD9A8 50%, var(--brand-gold-alt) 100%);
  }

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }
`;

const RecEyebrow = styled.div`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.12em;
  color: #A8863D;
  font-weight: 700;
  margin-bottom: 0.35rem;
`;

const RecReason = styled.div`
  font-size: 0.84rem;
  line-height: 1.55;
  color: #6B5A33;
  margin-bottom: 1.1rem;
  max-width: 42rem;
`;

const RecHeadline = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--brand-text);
  letter-spacing: -0.02em;
  margin-bottom: 0.35rem;

  @media (min-width: 768px) {
    font-size: 1.85rem;
  }
`;

const RecPriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const RecPerSeat = styled.div`
  font-size: 1rem;
  color: #333;
  font-weight: 500;
`;

const GoldPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #7A5E1E;
  background: rgba(200, 167, 94, 0.18);
  border: 1px solid rgba(200, 167, 94, 0.4);
`;

const TeamRatePill = styled(GoldPill)`
  color: #1E6B3A;
  background: #EAF7EE;
  border-color: rgba(39, 174, 96, 0.35);
`;

const MathStrip = styled.div`
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(200, 167, 94, 0.32);
  border-radius: 0.75rem;
  padding: 0.8rem 1rem;
  font-size: 0.92rem;
  color: #222;
  font-weight: 500;
  line-height: 1.5;
  margin-bottom: 1.1rem;
`;

const TermToggle = styled.div`
  display: inline-flex;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(200, 167, 94, 0.4);
  border-radius: 999px;
  padding: 0.2rem;
  margin-bottom: 1.1rem;
`;

const TermButton = styled.button<{ $active: boolean }>`
  border: none;
  border-radius: 999px;
  padding: 0.45rem 1.1rem;
  font-size: 0.82rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  text-transform: lowercase;
  background: ${(p) => (p.$active ? 'var(--brand-text)' : 'transparent')};
  color: ${(p) => (p.$active ? '#F5E9C9' : '#6B5A33')};

  &:hover {
    color: ${(p) => (p.$active ? '#F5E9C9' : 'var(--brand-text)')};
  }
`;

const StepperRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const StepBtn = styled.button`
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 50%;
  border: 1px solid rgba(200, 167, 94, 0.55);
  background: white;
  color: var(--brand-text);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  transition: all 0.12s ease;

  &:hover:not(:disabled) {
    background: #F7F0DD;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const SeatCount = styled.span`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--brand-text);
  min-width: 2rem;
  text-align: center;
`;

/* Task #70: seats are TYPED, not only stepped — hiring 40 Prest0ns should
   not take 35 clicks. Blur clamps to [floor, server max]. */
const SeatInput = styled.input`
  width: 4.5rem;
  text-align: center;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--brand-text);
  padding: 0.35rem 0.3rem;
  background: #FCFBF7;
  border: 1px solid #E4E0D5;
  border-radius: 0.6rem;

  &:focus { outline: none; border-color: var(--brand-gold); }
  /* No spinner arrows — the ± buttons are right there. */
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
`;

const StepperLabel = styled.span`
  font-size: 0.85rem;
  color: #555;
`;

const StepHint = styled.div`
  font-size: 0.75rem;
  color: #A8863D;
  margin-top: 0.4rem;
`;

/* Centered row for the bold calculator launcher (2026-08-19). Lives inside
 * RecCard, which clips overflow — the pill's soft shadow may crop at the card
 * edge, which is fine; the pill itself stays well inside. */
const CalcCtaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.9rem;
`;

const RecCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-text);
  color: #F5E9C9;
  border: none;
  border-radius: 0.625rem;
  padding: 0.8rem 1.75rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
  margin-top: 1.25rem;
  box-shadow: 0 0.25rem 0.9rem rgba(17, 17, 17, 0.18);

  &:hover:not(:disabled) {
    background: #2A2416;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RenewalLine = styled.div`
  font-size: 0.78rem;
  line-height: 1.55;
  color: #8A7648;
  margin-top: 0.8rem;
  max-width: 44rem;
`;

const QuietRow = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;
`;

const QuietAction = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.8rem;
  color: #6B5A33;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--brand-text);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AdminOnlyNote = styled.div`
  font-size: 0.85rem;
  color: #888;
  margin: 0 0 1.5rem;
  padding: 0 0.25rem;
`;

// ── The published ladder as a clickable explorer ──

const LadderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 1.1rem;
`;

const LadderRung = styled.button<{ $active?: boolean; $clickable?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.6rem 0.85rem;
  border-radius: 0.625rem;
  font-family: inherit;
  font-size: 0.84rem;
  transition: all 0.15s ease;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  background: ${(p) => (p.$active ? 'rgba(255,255,255,0.9)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(200,167,94,0.6)' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? '0 2px 8px rgba(176,141,61,0.12)' : 'none')};

  ${(p) =>
    p.$clickable &&
    css`
      &:hover {
        background: rgba(255, 255, 255, 0.75);
        border-color: rgba(200, 167, 94, 0.4);
      }
    `}
`;

const RungName = styled.span`
  color: #333;
  font-weight: 600;

  small {
    display: block;
    font-weight: 400;
    color: #8A7648;
    font-size: 0.72rem;
  }
`;

const RungPrice = styled.span`
  color: var(--brand-text);
  font-weight: 700;
  white-space: nowrap;

  small {
    font-weight: 400;
    color: #8A7648;
    font-size: 0.72rem;
    margin-left: 0.4rem;
  }
`;

// ── Personal trial strip ──

const TrialStrip = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
`;

const TrialStat = styled.div`
  flex: 1;
  min-width: 9.5rem;
  background: white;
  border: 1px solid #EDE6D2;
  border-radius: 0.75rem;
  padding: 0.85rem 1rem;
`;

const TrialStatLabel = styled.div`
  font-size: 0.7rem;
  text-transform: lowercase;
  letter-spacing: 0.07em;
  color: #A8863D;
  font-weight: 700;
  margin-bottom: 0.3rem;
`;

const TrialStatValue = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--brand-text);
`;

const TrialStatSub = styled.div`
  font-size: 0.72rem;
  color: #999;
  margin-top: 0.15rem;
`;

// ── Org-size chips (inline capture when nothing is on file) ──

const OrgChipRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin: 0.5rem 0 1.1rem;
`;

const OrgChip = styled.button<{ $active?: boolean }>`
  border-radius: 999px;
  padding: 0.4rem 0.95rem;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(p) => (p.$active ? 'var(--brand-text)' : 'white')};
  color: ${(p) => (p.$active ? '#F5E9C9' : '#555')};
  border: 1px solid ${(p) => (p.$active ? 'var(--brand-text)' : 'rgba(200,167,94,0.45)')};

  &:hover {
    border-color: var(--brand-text);
  }
`;

// ── Upgrade / change cards for active subscribers ──

/** One-click plan adjuster (2026-08-14): current subscribers change seats or
 *  cadence here — live math, one confirm, card on file. Never a checkout. */
const AdjustBox = styled.div`
  margin-top: 1.25rem;
  padding: 1.1rem 1.15rem 1.15rem;
  background: linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%);
  border: 1.5px solid rgba(198, 167, 94, 0.55);
  border-radius: 1rem;
`;

/* ── Trial header (2026-08-15 owner report) ────────────────────────────────
   A trial account arriving here saw "YOUR PLAN / No active plan" — technically
   true (there is no paid plan) but it reads as "nothing is set up", which is
   the opposite of what a running trial should say. This band leads the page
   for trial accounts and answers the four questions in order: what am I on,
   how long is left, what is left to spend, and what happens at the end. */
const TrialBand = styled.section<{ $tone: 'active' | 'soon' | 'ended' }>`
  max-width: 900px;
  margin: 0 auto 1.5rem;
  padding: 1.35rem 1.5rem 1.4rem;
  border-radius: 1.15rem;
  box-sizing: border-box;
  background: ${(p) =>
    p.$tone === 'ended'
      ? 'linear-gradient(135deg, #FBF1F1 0%, #FDFBFB 100%)'
      : 'linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%)'};
  border: 1.5px solid
    ${(p) =>
      p.$tone === 'ended' ? 'rgba(153, 27, 27, 0.35)' : 'rgba(198, 167, 94, 0.55)'};

  @media (max-width: 640px) {
    padding: 1.1rem 1rem 1.15rem;
    border-radius: 1rem;
  }
`;

const TrialBandEyebrow = styled.div<{ $tone: 'active' | 'soon' | 'ended' }>`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  color: ${(p) => (p.$tone === 'ended' ? '#9B2C2C' : 'var(--brand-gold-hover)')};
  margin-bottom: 0.4rem;
`;

const TrialBandTitle = styled.h2`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.15rem, 2.6vw, 1.5rem);
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--brand-text);
  margin: 0 0 0.4rem;
`;

const TrialBandLead = styled.p`
  font-size: 0.9rem;
  color: #5F5A52;
  line-height: 1.6;
  margin: 0 0 1rem;
  max-width: 46rem;
`;

const TrialBandNote = styled.p`
  font-size: 0.84rem;
  color: #6F6A60;
  line-height: 1.6;
  margin: 1rem 0 0;
  padding-top: 0.9rem;
  border-top: 1px solid rgba(198, 167, 94, 0.3);
`;

const AdjustHead = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  color: #8A6F2F;
  margin-bottom: 0.75rem;
`;

const AdjustPreviewLine = styled.div`
  font-size: 0.8rem;
  color: #4A4237;
  line-height: 1.55;
  margin: 0.6rem 0 0.75rem;
`;

const AdjustSuccess = styled.div`
  font-size: 0.82rem;
  color: #067647;
  line-height: 1.5;
  margin-top: 0.6rem;
`;

const ChangeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-top: 1.25rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ChangeCard = styled.div`
  border: 1px solid #EDE6D2;
  background: #FDFBF5;
  border-radius: 0.875rem;
  padding: 1.1rem 1.2rem;
`;

const ChangeTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 0.35rem;
`;

const ChangeText = styled.div`
  font-size: 0.82rem;
  line-height: 1.6;
  color: #666;
`;

const ChangeAction = styled.button`
  margin-top: 0.75rem;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  color: #7A5E1E;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: var(--brand-text);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================================
// Native hire confirm (task #73) — pays like the top-up popup
// ============================================================================

const PayMethodRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.45rem;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p.$active ? 'var(--brand-gold)' : '#E5E0D5')};
  background: ${(p) => (p.$active ? '#FBF7EC' : '#FFF')};
  font-family: inherit;
  font-size: 0.9rem;
  color: #222;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease;

  &:hover { border-color: var(--brand-gold); }
`;

const PayCardBox = styled.div`
  border: 1.5px solid #E5E0D5;
  border-radius: 10px;
  padding: 0.85rem 0.9rem;
  margin-bottom: 0.45rem;
  background: #FFF;
`;

const HireSummaryBox = styled.div`
  background: linear-gradient(135deg, #F7F3E8 0%, #FCFBF7 100%);
  border: 1px solid rgba(198, 167, 94, 0.4);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
`;

const HireSummaryTitle = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 0.3rem;
`;

const HireSummaryLine = styled.div`
  font-size: 0.82rem;
  color: #5A5346;
  line-height: 1.55;
`;

const PayError = styled.div`
  font-size: 0.82rem;
  color: #B3261E;
  line-height: 1.5;
  margin-top: 0.5rem;
`;

interface SavedPm { id: string; brand?: string; last4?: string; is_default?: boolean }

/**
 * The whole payment surface of the hire modal. Mirrors TopUpModal: saved
 * cards preselected (default card first), "use a new card" CardElement,
 * ONE confirm click. subscribeDirect creates the subscription
 * default_incomplete; confirmCardPayment settles the first invoice with
 * the chosen method; the page's activation poller does the rest. The buyer
 * never leaves the branded modal.
 */
const HireConfirmBody: React.FC<{
  tier: string;
  seats: number;
  /** Task #75: hire FOR a created organization (co_…) the caller owns —
   *  the plan lands on the org's own subscription, never the person's. */
  accountId?: string | null;
  /** The org's display name when hiring FOR an organization — names the
   *  buyer in the summary so whose plan this is can't be missed. */
  orgLabel?: string | null;
  onSuccess: () => void;
}> = ({ tier, seats, accountId, orgLabel, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const term: OfferTerm = tier.startsWith('wu_prepay') ? 'prepay' : tier === 'wu_monthly' ? 'monthly' : 'annual';
  const q = selectionPricing(term, seats);
  const totalMonthly = seats * q.monthlyUsdPerSeat;

  const [methods, setMethods] = useState<SavedPm[] | null>(null);
  const [selectedPmId, setSelectedPmId] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  // Task #80: cards members shared with the TARGET organization — listed
  // only when the server grants the view permission, and paying with one is
  // re-gated server-side on billing.payment-methods.use.
  const [sharedMethods, setSharedMethods] = useState<SharedPaymentMethod[]>([]);
  const [selectedSharedPmId, setSelectedSharedPmId] = useState<string | null>(null);

  // Idempotency attempt id (2026-08-18 audit): folded into the server-side
  // Stripe key so a double-click or retried request can never hire twice.
  // Kept across error retries; re-minted when the card selection changes
  // (tier/seats are fixed per modal and sit in the key server-side).
  const hireAttemptRef = useRef(stripeApi.newPaymentAttemptId());
  useEffect(() => {
    hireAttemptRef.current = stripeApi.newPaymentAttemptId();
  }, [selectedPmId, selectedSharedPmId, useNewCard]);

  useEffect(() => {
    let canceled = false;
    if (!accountId) { setSharedMethods([]); setSelectedSharedPmId(null); return; }
    void listOrgSharedPaymentMethods(accountId).then((cards) => {
      // Recurring hires can only draw from the org's own billing profile (a
      // pm only ever charges the customer holding it — 2026-08-18 audit), so
      // this modal lists just the subscription-usable shared cards. The
      // top-up modal keeps showing ALL shared cards.
      if (!canceled) setSharedMethods(cards.filter((c) => c.subscriptionUsable === true));
    });
    return () => { canceled = true; };
  }, [accountId]);

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const res = await stripeApi.listMethods();
        if (canceled) return;
        const cards = (res.methods || []) as SavedPm[];
        setMethods(cards);
        const preferred = cards.find((c) => c.is_default) ?? cards[0];
        if (preferred) setSelectedPmId(preferred.id);
        else setUseNewCard(true);
      } catch {
        if (!canceled) { setMethods([]); setUseNewCard(true); }
      }
    })();
    return () => { canceled = true; };
  }, []);

  const confirmHire = useCallback(async () => {
    setPayError(null);
    if (!stripe || !elements) {
      setPayError('payments are still loading — try again in a moment.');
      return;
    }
    if (!useNewCard && !selectedPmId && !selectedSharedPmId) {
      setPayError('choose a saved card or add a new one.');
      return;
    }
    setBusy(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('not signed in');
      const resp = await fetch(fnUrl('subscribeDirect'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier,
          seats,
          attemptId: hireAttemptRef.current,
          ...(accountId ? { accountId } : {}),
          ...(useNewCard || selectedSharedPmId
            ? {}
            : { paymentMethodId: selectedPmId }),
          ...(!useNewCard && selectedSharedPmId
            ? { sharedPaymentMethodId: selectedSharedPmId }
            : {}),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (data?.useChangeSubscription) {
          setPayError('you already have a plan — adjust your team in one click above instead.');
          return;
        }
        if (
          data?.code === 'pm-wrong-customer' ||
          data?.code === 'shared-card-not-billable-for-subscription' ||
          data?.code === 'shared-card-clone-failed' // pre-redesign server builds
        ) {
          setUseNewCard(true);
          setSelectedSharedPmId(null);
          setPayError(String(data?.error || 'use a new card for this organization.').toLowerCase());
          return;
        }
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      if (!data.clientSecret) {
        if (['active', 'trialing'].includes(data.status)) { onSuccess(); return; }
        throw new Error('could not start the payment — try again.');
      }
      const confirmResult = useNewCard
        ? await stripe.confirmCardPayment(data.clientSecret, {
            payment_method: { card: elements.getElement(CardElement)! },
          })
        : await stripe.confirmCardPayment(data.clientSecret, {
            // The server names the pm it applied (the CLONE id when a shared
            // card pays) — confirm with exactly that.
            payment_method: data.appliedPaymentMethodId || selectedPmId!,
          });
      if (confirmResult.error) {
        setPayError(confirmResult.error.message?.toLowerCase() || 'payment failed — try another card.');
        return;
      }
      onSuccess();
    } catch (e) {
      setPayError(((e as Error)?.message || 'something went wrong — try again.').toLowerCase());
    } finally {
      setBusy(false);
    }
  }, [stripe, elements, useNewCard, selectedPmId, selectedSharedPmId, tier, seats, accountId, onSuccess]);

  return (
    <>
      <HireSummaryBox>
        <HireSummaryTitle>
          {seats} Prest0n{seats > 1 ? 's' : ''} ·{' '}
          {term === 'prepay' ? 'prepaid year' : term === 'monthly' ? 'monthly' : 'annual — billed monthly'}
          {orgLabel ? ` · for ${orgLabel}` : ''}
        </HireSummaryTitle>
        <HireSummaryLine>
          {term === 'prepay' && q.upfrontTotalUsd != null
            ? <><strong>${q.upfrontTotalUsd.toLocaleString()}</strong> once for the year — {WU_PRICING.prepayDiscountPct}% off the rate</>
            : <><strong>${totalMonthly.toLocaleString()} / mo</strong> — ${q.monthlyUsdPerSeat.toLocaleString()} per Prest0n</>}
          {' · '}{(seats * q.wuPerSeat).toLocaleString()} {WU_LABEL} every month
        </HireSummaryLine>
        <HireSummaryLine>
          renews automatically at your team size · change it in one click before renewal ·
          nothing beyond your committed team ever bills without your administrator's approval
        </HireSummaryLine>
      </HireSummaryBox>

      <StepperLabel>payment method</StepperLabel>
      {methods === null ? (
        <HireSummaryLine>loading saved cards…</HireSummaryLine>
      ) : (
        <>
          {sharedMethods.length > 0 && (
            <>
              <HireSummaryLine>shared with this organization</HireSummaryLine>
              <HireSummaryLine>
                cards for recurring hires are saved to this organization's billing profile — enter a card once and it stays on file.
              </HireSummaryLine>
              {sharedMethods.map((c) => (
                <PayMethodRow
                  key={c.id}
                  type="button"
                  $active={!useNewCard && selectedSharedPmId === c.id}
                  onClick={() => {
                    setUseNewCard(false);
                    setSelectedPmId(null);
                    setSelectedSharedPmId(c.id);
                  }}
                >
                  <span>
                    {(c.brand || 'card').toLowerCase()} •••• {c.last4 || '????'}
                    {c.mine ? ' — your card' : c.ownerLabel ? ` — shared by ${c.ownerLabel}` : ' — shared'}
                  </span>
                  {!useNewCard && selectedSharedPmId === c.id && <span aria-hidden>✓</span>}
                </PayMethodRow>
              ))}
            </>
          )}
          {methods.map((c) => (
            <PayMethodRow
              key={c.id}
              type="button"
              $active={!useNewCard && selectedPmId === c.id}
              onClick={() => { setUseNewCard(false); setSelectedSharedPmId(null); setSelectedPmId(c.id); }}
            >
              <span>{(c.brand || 'card').toLowerCase()} •••• {c.last4 || '????'}{c.is_default ? ' · default' : ''}</span>
              {!useNewCard && selectedPmId === c.id && <span aria-hidden>✓</span>}
            </PayMethodRow>
          ))}
          <PayMethodRow type="button" $active={useNewCard} onClick={() => setUseNewCard(true)}>
            <span>use a new card</span>
            {useNewCard && <span aria-hidden>✓</span>}
          </PayMethodRow>
          {useNewCard && (
            <PayCardBox>
              <CardElement options={{ hidePostalCode: true }} />
            </PayCardBox>
          )}
        </>
      )}

      <RecCta onClick={confirmHire} disabled={busy || methods === null} style={{ marginTop: '0.9rem' }}>
        {busy
          ? 'processing…'
          : term === 'prepay' && q.upfrontTotalUsd != null
            ? `prepay the year — $${q.upfrontTotalUsd.toLocaleString()}`
            : `make the hire — $${totalMonthly.toLocaleString()} / mo`}
      </RecCta>
      {payError && <PayError role="alert">{payError}</PayError>}
      <StepHint style={{ marginTop: '0.6rem' }}>
        your payment is processed securely. your Prest0n{seats > 1 ? 's start' : ' starts'} the
        moment it goes through.
      </StepHint>
    </>
  );
};

// ============================================================================
// Component
// ============================================================================

const Subscription: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isTestMode, setIsTestMode] = useState(() => shouldTagAsTest());

  // ── Checkout redirect handling ──
  const [checkoutPending, setCheckoutPending] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has('session_id');
  });
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAttemptsRef = useRef(0);

  // ── One-click plan changes for existing subscribers (2026-08-14 owner
  // directive: hire more / fire Prest0ns / switch terms — friendly end to
  // end, charged to the payment method on file, never a fresh checkout) ──
  const [adjSeats, setAdjSeats] = useState<number>(1);
  const [adjTerm, setAdjTerm] = useState<OfferTerm>('annual');
  const adjTouched = useRef(false);
  const [adjPreview, setAdjPreview] = useState<{
    loading: boolean;
    isUpgrade?: boolean;
    amountDueNowCents?: number;
    effectiveAt?: string;
    periodEnd?: number | null;
  } | null>(null);
  const [adjApplying, setAdjApplying] = useState(false);
  const [adjResult, setAdjResult] = useState<string | null>(null);

  // ── In-page hire confirm (task #73): the native, top-up-grade popup ──
  // A non-null selection opens our branded modal (HireConfirmBody) — saved
  // card preselected, one confirm. On success the modal closes and the
  // existing activation poller takes over (the webhook flips the plan
  // active).
  const [hireSel, setHireSel] = useState<{ tier: string; seats: number } | null>(null);
  // Org-targeted hire (task #75): '/subscription?account=co_…' — the
  // onboarding completion for a hire-required created org lands here; every
  // hire on this visit targets THAT org. Only created-org ids are honored
  // (the server re-verifies ownership regardless).
  const [targetOrgAccountId, setTargetOrgAccountId] = useState<string | null>(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('account');
      return v && v.startsWith('co_') ? v : null;
    } catch { return null; }
  });
  // Task #80: when a billing.manage member manages an ORG's billing, the
  // page's `subscription` state is seeded from the workspace payload — this
  // ref keeps the personal fetchStatus from clobbering it.
  const orgManageRef = useRef(false);
  /**
   * WHOSE billing this page is showing — and the gate that stopped the page
   * contradicting itself (owner report 2026-08-25: "the user sees one thing
   * and it takes several seconds for them to see conflicting information").
   *
   * The personal status fetch and the workspace resolution used to race: the
   * personal call is one HTTP hop and always won, so an org-scoped viewer
   * watched their OWN plan paint, then get replaced seconds later by the
   * org's plan, the read-only view, or the permission wall. Now the personal
   * fetch waits until the scope is known, so only ONE of them ever paints.
   *
   * Seeded synchronously from the shared snapshot cache, so a repeat visit
   * knows the scope in the first frame and waits for nothing.
   */
  const [billingScope, setBillingScope] = useState<'unknown' | 'personal' | 'org'>(
    () => readCachedBillingSnapshot(user?.id)?.scope ?? 'unknown',
  );
  // Ref indirection: handleHireSuccess is declared before refreshOrgBilling
  // exists (hook order), so it reaches the refresher through this ref.
  const refreshOrgBillingRef = useRef<() => Promise<void>>(async () => {});
  // Task #77 (2026-08-18 owner directive): members without billing access
  // still need an ALTERNATIVE — one click routes a top-up / plan-change /
  // hire request to the workspace owner through the standing notification
  // channels. Server-verified membership, rate-limited, no billing data
  // disclosed to the requester.
  const [billingRequest, setBillingRequest] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const sendBillingRequest = useCallback(async (accountId: string, kind: 'top_up' | 'plan_change' | 'hire') => {
    setBillingRequest('sending');
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const fn = httpsCallable(getFunctions(getApp()), 'requestBillingAttention');
      await fn({ accountId, kind });
      setBillingRequest('sent');
    } catch {
      setBillingRequest('failed');
    }
  }, []);

  const handleHireSuccess = useCallback(() => {
    setHireSel(null);
    // The cached snapshot both pages paint from is now wrong — drop it so the
    // next visit re-reads rather than flashing the pre-hire offer.
    invalidateBillingSnapshot(user?.id);
    if (orgManageRef.current) {
      // Org-manage hire (task #80): the org's billing is already THIS page's
      // surface — re-pull the org facts instead of bouncing to /desktop.
      setTimeout(() => { void refreshOrgBillingRef.current(); }, 1200);
      return;
    }
    if (targetOrgAccountId) {
      // The org's own webhook flips it hired and provisions its VM — the
      // uid-keyed poller can't see that doc, so open the workspace, which
      // renders the honest boot state.
      setTimeout(() => { window.location.assign('/desktop'); }, 1200);
      return;
    }
    pollAttemptsRef.current = 0;
    setCheckoutPending(true);
  }, [targetOrgAccountId, user?.id]);

  // Bootstrap: read testing_environment from Firestore user settings on mount,
  // since the runtime flag only gets set when Settings.tsx has loaded in this session.
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const { doc, getDoc, getFirestore } = await import('firebase/firestore');
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'users', fbUser.uid, 'user_settings', 'user_settings'));
        if (canceled || !snap.exists()) return;
        const data = snap.data() as any;
        const first = Array.isArray(data?.profile_settings) ? data.profile_settings[0] : null;
        if (first?.testing_environment === true) {
          setRuntimeTestingEnvironment(true);
          setIsTestMode(true);
        }
      } catch { /* non-fatal */ }
    })();
    // Also listen for live toggle changes from Settings page
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.testingEnvironment === 'boolean') {
        setIsTestMode(detail.testingEnvironment || shouldTagAsTest());
      }
    };
    window.addEventListener('testing-environment-changed', handler);
    return () => { canceled = true; window.removeEventListener('testing-environment-changed', handler); };
  }, []);

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // §6 role×billing matrix (multi-org P7): resolve whether this page should
  // show the ORG's subscription universe instead of the caller's own.
  //
  // 2026-08-17 owner directive (Report C): a member holding the workspace's
  // billing permission sees the FULL subscription surface for the active
  // workspace — the same read detail the owner sees — so the 'view' payload
  // now carries the full facts (plan/seats/term/renewal/trial state/paying-
  // wallet figures). Every new field is optional: an older functions
  // deployment still renders the coarse card instead of a dead end. Writes
  // stay exactly as gated before — this is READ visibility only.
  const [orgBilling, setOrgBilling] = useState<
    | null
    | { mode: 'wall'; orgLabel: string; helpers: string | null; accountId: string }
    | {
        mode: 'view';
        orgLabel: string;
        accountId: string;
        canManage: boolean;
        /** Server-verified ownership from the workspace option (the pill's
         *  "yours" source) — reaches 'view' mode only for hired co_ orgs. */
        isOwner: boolean;
        sub: {
          plan: string | null;
          status: string | null;
          isTrial: boolean;
          seatCount: number | null;
          planAmountUsd?: number | null;
          planInterval?: string | null;
          planWuPerSeatMonthly?: number | null;
          planCommitmentMonths?: number | null;
          currentPeriodEnd?: number | null;
          aiEnabled?: boolean;
          graceExpiresAt?: number | null;
          isTest?: boolean;
          isClientFree?: boolean;
          trialExpiresAtMs?: number | null;
        } | null;
        seatLimit: { maxSeats: number | null; source: string } | null;
        trial: {
          active: boolean;
          tier: string | null;
          creditAmountCents: number | null;
          expiresAtMs: number | null;
        } | null;
        wallet: { olvBalance: number | null; totalOlvSpent: number | null } | null;
        orgSizeBand: string | null;
      }
  >(null);
  useEffect(() => {
    let canceled = false;
    // Every exit below records whose billing this page shows; the personal
    // fetch is gated on it, so nothing paints before the answer is known.
    const markScope = (scope: 'personal' | 'org') => {
      if (!canceled) setBillingScope(scope);
    };
    (async () => {
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) { markScope('personal'); return; }
        const { fetchWorkspaceOptionsCached } = await import('../services/workspaceOptionsCache');
        const { workspaceLabel } = await import('../services/workspaceIdentity');
        const options = await fetchWorkspaceOptionsCached(fbUser.uid);
        // Org stickiness round 2 (2026-08-19): no active flag → this
        // device's last CONFIRMED org for this user (never an arbitrary row).
        const { readConfirmedWorkspace } = await import('../services/prest0nRuntime/confirmedWorkspaceCache');
        const lastKnownId = readConfirmedWorkspace(fbUser.uid)?.accountId ?? null;
        const active =
          options.find((o) => o.isActive) ||
          options.find((o) => o.accountId === lastKnownId) ||
          null;
        // Own workspace (or unresolved): the page keeps its historical body.
        if (!active || active.accountId === fbUser.uid) { markScope('personal'); return; }
        // OWNER of the active workspace — the SAME server-verified isOwner
        // the pill's "yours" badge renders (owner report 2026-08-18: the
        // owner of a slug-keyed org like Prest0 Inc was shown the §6 member
        // view telling them to "ask the owner"). For uid-keyed and legacy
        // slug-keyed workspaces the org's billing IS the owner's uid-keyed
        // surface, so the full historical page is the org's billing page.
        // Created (co_) orgs stay on the org-scoped path below — their plan
        // doc is org-keyed.
        if (active.isOwner && !active.accountId.startsWith('co_')) { markScope('personal'); return; }
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp } = await import('firebase/app');
        const fn = httpsCallable<{ accountId: string }, any>(
          getFunctions(getApp()), 'getWorkspaceSubscriptionStatus',
        );
        const orgLabel = workspaceLabel(active, fbUser.uid);
        try {
          const res = await fn({ accountId: active.accountId });
          if (canceled) return;
          // Un-hired CREATED org, viewed by someone who can manage its
          // billing (the owner): the read-only workspace-billing view has no
          // purchase CTA and its "ask the owner" copy would be shown TO the
          // owner — the one person who must hire here. Render the HIRE
          // surface instead, targeted at this org, whether the visit came
          // from onboarding's ?account= handoff or a plain /subscription
          // visit later (task #75 review findings 2/16).
          const orgSub: any = res.data?.subscription ?? null;
          // hasStripeSubscription is the callable's presence BOOLEAN — the raw
          // Stripe id deliberately never reaches this payload, so keying on it
          // here always read false (task #80 fix F5: hired co_ owners were
          // bounced to the hire surface forever).
          const orgSubLive = orgSub?.hasStripeSubscription === true
            && ['active', 'trialing', 'past_due'].includes(String(orgSub?.status || ''));
          if (
            active.accountId.startsWith('co_')
            && res.data?.canManage === true
            && !orgSubLive
          ) {
            setTargetOrgAccountId(active.accountId);
            markScope('org');
            return;
          }
          markScope('org');
          setOrgBilling({
            mode: 'view',
            orgLabel,
            accountId: active.accountId,
            canManage: res.data?.canManage === true,
            isOwner: active.isOwner === true,
            sub: res.data?.subscription ?? null,
            seatLimit: res.data?.seatLimit ?? null,
            // Full-detail read (2026-08-17) — absent on older deployments.
            trial: res.data?.trial ?? null,
            wallet: res.data?.wallet ?? null,
            orgSizeBand: typeof res.data?.orgSizeBand === 'string' ? res.data.orgSizeBand : null,
          });
          // Task #80 — MANAGE mode: a billing.manage member gets the full
          // owner surface, operating on the ORG's subscription. Seed the
          // page's plan state from the workspace payload (same facts the
          // owner path reads from getSubscriptionStatus) and pin it so the
          // personal fetch never clobbers it.
          if (res.data?.canManage === true && orgSub) {
            orgManageRef.current = true;
            setSubscription(wsSubToInfo(orgSub));
            setLoading(false);
          } else if (res.data?.canManage === true) {
            // 2026-08-18 audit (seeding gap): a slug/uid org with NO
            // subscription payload, viewed by a billing.manage member, used
            // to fall through — orgManage computed true while `subscription`
            // stayed the viewer's PERSONAL plan, so the page showed the
            // admin their own plan as if it were the org's. Pin manage mode
            // with a null plan: the offer tiles render (org-no-plan) and the
            // hire targets the org via billingTarget. (Un-hired co_ orgs
            // already routed to the hire surface above.)
            orgManageRef.current = true;
            setSubscription(null);
            setLoading(false);
          }
        } catch (err: any) {
          if (canceled) return;
          // permission-denied → the §6 wall; anything else (older functions
          // deployment) keeps the historical page rather than a dead end.
          const code = String(err?.code || '');
          if (code.includes('permission-denied')) {
            markScope('org');
            setOrgBilling({ mode: 'wall', orgLabel, helpers: null, accountId: active.accountId });
          } else {
            // Older functions deployment — the historical personal page.
            markScope('personal');
          }
        }
      } catch { markScope('personal'); /* fail-open to the historical page */ }
    })();
    return () => { canceled = true; };
  }, []);
  const [error, setError] = useState<string | null>(null);

  // Billing permission (owner or billing.manage — additive, false until RBAC resolves).
  const usageVisibility = useUsageVisibility();
  // Live OLV rate — getSubscriptionStatus returns totalSpent in OLV units.
  const rateCtx = useMaybeOLVRate();
  // Trial personalization (credit remaining / days left) for trial accounts.
  const trialStatus = useTrialStatus();

  // Org-size band (users/{uid}.organization_employee_count, written by the
  // wallet profile card AND by the inline chips below) → drives the plan
  // recommendation and the published deployment minimum.
  const [orgBand, setOrgBand] = useState<OrgSizeBand | null>(null);
  const [orgBandLoaded, setOrgBandLoaded] = useState(false);
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const { doc, getDoc, getFirestore } = await import('firebase/firestore');
        const snap = await getDoc(doc(getFirestore(), 'users', fbUser.uid));
        if (canceled) return;
        const raw = snap.exists() ? (snap.data() as any)?.organization_employee_count : null;
        if (typeof raw === 'string' && raw) setOrgBand(raw as OrgSizeBand);
      } catch { /* non-fatal — defaults to the single-Prest0n recommendation */ }
      finally { if (!canceled) setOrgBandLoaded(true); }
    })();
    return () => { canceled = true; };
  }, [user]);

  // Inline org-size capture — the recommendation personalizes the moment the
  // user answers, and the answer is saved as the same account metadata the
  // wallet profile card writes.
  const handleSelectOrgBand = useCallback(async (band: OrgSizeBand) => {
    setOrgBand(band);
    try {
      const fbUser = getAuth().currentUser;
      if (!fbUser) return;
      const { doc, setDoc, getFirestore } = await import('firebase/firestore');
      await setDoc(
        doc(getFirestore(), 'users', fbUser.uid),
        { organization_employee_count: band },
        { merge: true },
      );
    } catch { /* non-fatal — the selection still personalizes this session */ }
  }, []);

  // Recommendation + explorer state. First offer is ALWAYS the annual plan
  // (owner directive) at the published minimum for the org's size on file.
  const rec = useMemo(() => recommendPlan(orgBand), [orgBand]);
  const [term, setTerm] = useState<OfferTerm>('annual');
  const [recSeats, setRecSeats] = useState<number>(() => rec.seats);
  useEffect(() => { setRecSeats(rec.seats); }, [rec.seats, rec.tier]);

  /**
   * One transient failure used to be terminal: this ran once per `user`
   * change, and any hiccup (token not yet rehydrated, a cold-start 5xx) left
   * the page showing "No active plan" plus a red error for the rest of the
   * session. Retry with backoff, and only surface an error once the retries
   * are spent.
   */
  const fetchStatus = useCallback(async () => {
    if (!user) return;
    // Org-manage mode renders the ORG's subscription — the caller's personal
    // plan must never overwrite it (task #80).
    if (orgManageRef.current) return;
    setLoading(true);
    setError(null);
    const MAX_ATTEMPTS = 3;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      try {
        const token = await getAuthToken();
        if (!token) throw new Error('Not signed in');
        const resp = await fetch(fnUrl('getSubscriptionStatus'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setSubscription(data.subscription ?? null);
        setError(null);
        setLoading(false);
        return;
      } catch {
        if (attempt === MAX_ATTEMPTS) {
          setError('Failed to load subscription info');
        } else {
          await new Promise((r) => setTimeout(r, 600 * attempt));
        }
      }
    }
    setLoading(false);
  }, [user]);

  /**
   * The personal read waits for the scope decision — that is what stops an
   * org-scoped viewer seeing their own plan first and the org's a few seconds
   * later. On a repeat visit the scope is already seeded from cache, so this
   * waits for nothing.
   *
   * Liveness backstop: if the workspace resolution never answers (a hung
   * callable, an offline device), fall through to the personal read rather
   * than leave the page on "loading…" forever. Worst case that is the old
   * behavior, delayed — never a permanent stall.
   */
  useEffect(() => {
    if (billingScope === 'org') return undefined;
    if (billingScope === 'personal') { fetchStatus(); return undefined; }
    const timer = setTimeout(() => setBillingScope('personal'), SCOPE_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [fetchStatus, billingScope]);

  // Poll until subscription is active after Stripe Checkout redirect
  useEffect(() => {
    if (!checkoutPending) return;

    const MAX_ATTEMPTS = 24; // ~2 minutes at 5s intervals
    const INTERVAL_MS = 5000;

    const poll = async () => {
      pollAttemptsRef.current += 1;
      if (!user) {
        // Not signed in yet — retry shortly
        if (pollAttemptsRef.current < MAX_ATTEMPTS) {
          pollTimerRef.current = setTimeout(poll, INTERVAL_MS);
        } else {
          setCheckoutPending(false);
        }
        return;
      }
      try {
        const token = await getAuthToken();
        if (!token) throw new Error('No token');
        const resp = await fetch(fnUrl('getSubscriptionStatus'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const sub = data.subscription;
        if (sub && ['active', 'trialing'].includes(sub.status)) {
          // Active — clear URL param and show normal view
          setSubscription(sub);
          setCheckoutPending(false);
          const url = new URL(window.location.href);
          url.searchParams.delete('session_id');
          window.history.replaceState({}, '', url.toString());
          return;
        }
      } catch { /* retry */ }

      if (pollAttemptsRef.current < MAX_ATTEMPTS) {
        pollTimerRef.current = setTimeout(poll, INTERVAL_MS);
      } else {
        // Timed out — fall through to normal view
        setCheckoutPending(false);
        fetchStatus();
      }
    };

    pollTimerRef.current = setTimeout(poll, 1500);

    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [checkoutPending, user, fetchStatus]);

  const handleSubscribe = useCallback(async (tier: string = 'starter', seats?: number, forceTestMode?: boolean) => {
    setActionLoading(true);
    setError(null);
    invalidateBillingSnapshot(user?.id);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not signed in');
      const useTestMode = forceTestMode ?? isTestMode;

      if (useTestMode) {
        // Test mode: create simulated subscription (no Stripe charge)
        const resp = await fetch(fnUrl('createStarterSubscription'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ testMode: true, tier }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(body?.error || `HTTP ${resp.status}`);
        }
        const data = await resp.json();
        if (data.alreadySubscribed) {
          await fetchStatus();
          return;
        }
        await fetchStatus();
      } else {
        // Production (task #73): open OUR branded hire confirm — the saved
        // card preselected, one click. No Stripe-hosted surface, ever.
        setHireSel({ tier, seats: typeof seats === 'number' ? seats : 1 });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to start subscription');
    } finally {
      setActionLoading(false);
    }
  }, [fetchStatus, isTestMode, user?.id]);

  // ── Task #80: org-manage mode — billing.manage members get the full owner
  // surface, every write targeted at the ORG's billing. ──
  const orgManage = orgBilling?.mode === 'view' && orgBilling.canManage === true;
  const billingTarget = orgManage
    ? { accountId: orgBilling.accountId, label: orgBilling.orgLabel }
    : targetOrgAccountId
      ? { accountId: targetOrgAccountId, label: 'your new organization' }
      : null;
  // Whose card the plan-change copy names (2026-08-18 audit): under
  // org-manage the §banner already says the ORG's card pays — "your card on
  // file" in the adjuster/preview/result lines contradicted it.
  const cardOnFileLabel = orgManage && orgBilling?.orgLabel
    ? `${orgBilling.orgLabel}'s card on file`
    : 'your card on file';

  // In org-manage mode the workspace-scoped canManage IS the authority; the
  // personal RBAC walk (usageVisibility) gates only the self-billing surface.
  const canManageHere = orgManage ? true : usageVisibility.canManageBilling;

  /** Re-pull the org's facts after an applied change or hire. */
  const refreshOrgBilling = useCallback(async () => {
    if (!orgBilling || orgBilling.mode !== 'view') return;
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { getApp } = await import('firebase/app');
      const fn = httpsCallable(getFunctions(getApp()), 'getWorkspaceSubscriptionStatus');
      const res: any = await fn({ accountId: orgBilling.accountId });
      const orgSub: any = res.data?.subscription ?? null;
      setOrgBilling((prev) =>
        prev && prev.mode === 'view'
          ? {
              ...prev,
              sub: orgSub,
              seatLimit: res.data?.seatLimit ?? prev.seatLimit,
              trial: res.data?.trial ?? prev.trial,
              wallet: res.data?.wallet ?? prev.wallet,
            }
          : prev
      );
      if (res.data?.canManage === true && orgSub) setSubscription(wsSubToInfo(orgSub));
    } catch { /* non-fatal — the page keeps the last-known facts */ }
  }, [orgBilling]);
  refreshOrgBillingRef.current = refreshOrgBilling;

  // Seed the adjust panel from the live plan (until the user touches it).
  useEffect(() => {
    if (adjTouched.current || !subscription?.plan?.startsWith('wu_')) return;
    setAdjSeats(subscription.planSeats ?? 1);
    setAdjTerm(
      subscription.plan === 'wu_monthly' ? 'monthly'
        : subscription.plan.startsWith('wu_prepay') ? 'prepay'
          : 'annual'
    );
  }, [subscription]);

  const currentTerm: OfferTerm = subscription?.plan === 'wu_monthly' ? 'monthly'
    : subscription?.plan?.startsWith('wu_prepay') ? 'prepay' : 'annual';
  const adjDirty = Boolean(
    subscription?.plan?.startsWith('wu_')
    && (adjSeats !== (subscription?.planSeats ?? 1) || adjTerm !== currentTerm)
  );

  // Debounced server preview: what changes, what's due now, when it lands.
  useEffect(() => {
    if (!adjDirty) { setAdjPreview(null); return; }
    let canceled = false;
    setAdjPreview({ loading: true });
    const t = setTimeout(async () => {
      try {
        const token = await getAuthToken();
        if (!token) throw new Error('Not signed in');
        const resp = await fetch(fnUrl('changeSubscription'), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode: 'preview',
            term: adjTerm,
            seats: adjSeats,
            ...(billingTarget ? { accountId: billingTarget.accountId } : {}),
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (canceled) return;
        if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
        setAdjPreview({
          loading: false,
          isUpgrade: data.isUpgrade,
          amountDueNowCents: data.amountDueNowCents,
          effectiveAt: data.effectiveAt,
          periodEnd: data.periodEnd,
        });
      } catch {
        if (!canceled) setAdjPreview({ loading: false });
      }
    }, 500);
    return () => { canceled = true; clearTimeout(t); };
  }, [adjDirty, adjTerm, adjSeats, billingTarget?.accountId]);

  const applyPlanChange = useCallback(async () => {
    setAdjApplying(true);
    setAdjResult(null);
    setError(null);
    // Invalidate up front: whatever the outcome, the cached seats/term can no
    // longer be trusted to describe this workspace.
    invalidateBillingSnapshot(user?.id);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not signed in');
      const resp = await fetch(fnUrl('changeSubscription'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'apply',
          term: adjTerm,
          seats: adjSeats,
          ...(billingTarget ? { accountId: billingTarget.accountId } : {}),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        // Card on file declined / no live subscription — the native hire
        // confirm (task #73) is the graceful fallback, never a dead end:
        // same branded popup, pick a card, one click.
        if (data?.needsCheckout) {
          const sel2 = selectionPricing(adjTerm, adjSeats);
          await handleSubscribe(sel2.tier, adjSeats);
          return;
        }
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }
      adjTouched.current = false;
      setAdjResult(
        data.effectiveAt === 'now'
          ? `done — your team is updated, and the prorated difference goes to ${cardOnFileLabel}.`
          : `done — this change is scheduled for your renewal${data.periodEnd ? ` on ${formatDate(data.periodEnd)}` : ''}. nothing changes until then.`
      );
      await fetchStatus();
      // Org-manage mode: the personal fetch no-ops — re-pull the ORG facts.
      await refreshOrgBilling();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to change subscription');
    } finally {
      setAdjApplying(false);
    }
  }, [adjTerm, adjSeats, fetchStatus, handleSubscribe, billingTarget?.accountId, refreshOrgBilling, user?.id]);

  const handleManage = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    // Stripe's portal can cancel or change the plan outside this app; the
    // return lands back here, so the cached answer must not survive the trip.
    invalidateBillingSnapshot(user?.id);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not signed in');
      const resp = await fetch(fnUrl('createCustomerPortalSession'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ returnUrl: window.location.href }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      if (data.url) window.location.href = data.url;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to open billing portal');
    } finally {
      setActionLoading(false);
    }
  }, [user?.id]);

  // ── Derived state ──

  const statusVariant = (): StatusBadgeProps['variant'] => {
    if (!subscription) return 'inactive';
    const s = subscription.status;
    if (s === 'active') return subscription.is_trial ? 'trialing' : 'active';
    if (s === 'trialing') return 'trialing';
    if (s === 'past_due') return 'past_due';
    if (s === 'canceled') return 'canceled';
    return 'inactive';
  };

  const statusLabel = (): string => {
    if (!subscription) return 'No Plan';
    const s = subscription.status;
    if (s === 'active') return subscription.is_trial ? 'Trial' : 'Active';
    if (s === 'trialing') return 'Trial';
    if (s === 'past_due') return 'Past Due';
    if (s === 'canceled') return 'Canceled';
    return s;
  };

  const planDisplay = subscription ? (PLAN_DISPLAY[subscription.plan] || PLAN_DISPLAY.starter) : null;

  const hasActiveSubscription = subscription &&
    ['active', 'trialing', 'past_due'].includes(subscription.status);
  /**
   * Trial state has two sources, and the resilient one has to win when the
   * status call comes back empty. `subscription` is a one-shot HTTP read;
   * `trialStatus` is a live Firestore listener that self-heals as auth
   * settles. Keying trial UI on the HTTP payload alone meant a failed status
   * call showed a trial account "No active plan" (owner report 2026-08-15).
   * A loaded subscription still wins outright — a converted account whose doc
   * says is_trial:false must never be pulled back into trial UI by a stale
   * users/{uid}.trial field.
   */
  const isTrialAccount =
    subscription?.is_trial === true || (subscription == null && trialStatus.isTrialUser);

  /** Drives the trial band's palette and copy: calm, warning, or ended. */
  const trialTone: 'active' | 'soon' | 'ended' = trialStatus.isExpired
    ? 'ended'
    : trialStatus.isExpiringSoon
      ? 'soon'
      : 'active';
  // A PAID plan (not a trial shell) — the posture that gets upgrade/downgrade
  // cards instead of the fresh-hire presentation.
  const hasPaidPlan = Boolean(hasActiveSubscription && !isTrialAccount);

  // Interval-aware active-plan price line. Since the 2026-08-14 cadence
  // change, a 'year' interval means the year was PREPAID in one payment
  // (annual hires bill monthly); the doc stores the amount actually charged.
  const activePriceLine = (() => {
    const amt = subscription?.planAmountUsd;
    if (typeof amt !== 'number' || amt <= 0) return '';
    const seatCount = subscription?.planSeats ?? 1;
    const seatsSuffix = seatCount > 1 ? ` × ${seatCount} Prest0ns` : '';
    if (subscription?.planInterval === 'year') {
      const monthlyEq = Math.round(amt / 12);
      return `$${monthlyEq.toLocaleString()} / mo per Prest0n${seatsSuffix} · prepaid year — `;
    }
    if (subscription?.plan?.startsWith('wu_annual')) {
      return `$${amt.toLocaleString()} / mo per Prest0n${seatsSuffix} · billed monthly, 12-month hire — `;
    }
    return `$${amt} / month — `;
  })();

  // ── Offer gating ──

  // totalSpent arrives in OLV units; convert at the live rate. If the rate is
  // not yet known, stay at 0 — conservative: a trial account under the reveal
  // threshold must never see the upsell.
  const totalSpentUsd = useMemo(() => {
    const spent = subscription?.totalSpent;
    if (typeof spent !== 'number' || !isFinite(spent) || spent <= 0) return 0;
    const rate = rateCtx?.state.rateUsd;
    if (typeof rate !== 'number' || !isFinite(rate) || rate <= 0) return 0;
    return rateCtx!.olvToUsd(spent);
  }, [subscription, rateCtx]);

  const offersRevealed = !loading && shouldRevealPlanOffers({
    isTrial: isTrialAccount,
    totalSpentUsd,
    // Owner decision 8 (2026-08-16): once the trial window ends, the upgrade
    // is always presented — the reveal threshold only applies mid-trial.
    trialEnded: trialStatus.isExpired,
  });

  const currentTierRank = subscription ? (ANNUAL_TIER_RANK[subscription.plan] ?? 0) : 0;
  const alreadyOnRecommendedAnnual = Boolean(
    hasPaidPlan && currentTierRank >= (ANNUAL_TIER_RANK[rec.tier] ?? 1)
  );

  // The fresh-hire offer card: trial accounts past the reveal + anyone with no
  // paid plan. Paid subscribers get the change cards instead.
  // OPTIMISTIC while permissions load (2026-08-14 load-speed pass): the RBAC
  // check is a serial four-read Firestore waterfall, and gating the card on
  // it made the page feel slow — the offer popped in seconds late. Render
  // immediately; only swap to the admin note once permissions have RESOLVED
  // to "can't manage billing". Checkout itself stays safe — Stripe payment
  // is a deliberate act and the server verifies the signed-in user.
  // An org-targeted hire visit ALWAYS shows the plan tiles — the caller's
  // personal plan state (hasPaidPlan) is irrelevant to hiring for the org,
  // and hiding the tiles behind it dead-ended every personally-subscribed
  // owner (task #75 review finding 16).
  const showOfferCard =
    // Un-hired created-org visit: ALWAYS the tiles (task #75 review finding 16
    // — the caller's personal plan state is irrelevant to hiring for the org).
    targetOrgAccountId != null ||
    // Org-manage mode: the tiles show when the ORG has no paid plan.
    (orgManage && !hasPaidPlan) ||
    (!orgManage && offersRevealed && !hasPaidPlan &&
      (usageVisibility.canManageBilling || !usageVisibility.ready));
  // Members without billing permission see one quiet line instead of the card.
  // Never shown to an org manager — their capability is workspace-scoped.
  const showAdminOnlyNote =
    !orgManage && offersRevealed && !hasPaidPlan &&
    usageVisibility.ready && !usageVisibility.canManageBilling;

  // Explorer math — every number on the card recomputes from term + seats.
  const minSeats = rec.minSeats;
  const sel = selectionPricing(term, recSeats);
  const teamRateApplied =
    term !== 'monthly' && (ANNUAL_TIER_RANK[sel.tier] ?? 1) > (ANNUAL_TIER_RANK[rec.tier] ?? 1);
  const totalMonthly = recSeats * sel.monthlyUsdPerSeat;
  const totalWu = recSeats * sel.wuPerSeat;
  // Only the PREPAY term ever charges the year in one payment — 20% off the
  // seat-count rate (2026-08-16 owner directive).
  const prepayTotal = sel.upfrontTotalUsd ?? totalMonthly * 12;

  // Trial personalization numbers (value-before-price strip).
  const trialWuUsed = usdToWu(totalSpentUsd);
  const trialCreditRemainingUsd =
    trialStatus.isTrialUser && typeof trialStatus.creditRemainingCents === 'number'
      ? trialStatus.creditRemainingCents / 100
      : null;

  // ── Renders ──

  // §6 role×billing matrix (multi-org P7, 2026-08-16): acting inside an
  // organization workspace that is not your own, the page shows THAT org's
  // subscription universe — a permission wall naming who can help when you
  // lack billing.view, or the coarse org-subscription card when you hold it.
  // Changes stay with the owner's own billing surfaces until D8/M5 moves
  // Stripe customers onto workspaces. Your own workspace: page unchanged.
  if (orgBilling?.mode === 'wall') {
    return (
      <PageContainer>
        <PageTitle>subscription</PageTitle>
        <Section>
          <SetupTitle>billing for {orgBilling.orgLabel} is managed by its owner</SetupTitle>
          <SetupSub data-org-billing-wall="">
            you&rsquo;re working inside {orgBilling.orgLabel}&rsquo;s workspace, and your role
            here doesn&rsquo;t include billing visibility. the organization&rsquo;s owner
            {orgBilling.helpers ? ` (${orgBilling.helpers})` : ''} and anyone holding the
            billing permission can view or change its plan — ask them about plans, seats, or
            invoices. your own organization&rsquo;s subscription is available by switching to
            your workspace in the top-right menu.
          </SetupSub>
          {/* Task #77: the honest ALTERNATIVE for members without billing
              access — one click notifies the owner; no billing data shown. */}
          <div style={{ marginTop: '0.9rem', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {billingRequest === 'sent' ? (
              <span style={{ fontSize: '0.85rem', color: '#4B7B5D' }} data-testid="billing-request-sent">
                sent — the owner&rsquo;s been notified.
              </span>
            ) : (
              <>
                <SecondaryButton
                  type="button"
                  data-testid="billing-request-topup"
                  disabled={billingRequest === 'sending'}
                  onClick={() => { void sendBillingRequest(orgBilling.accountId, 'top_up'); }}
                >
                  {billingRequest === 'sending' ? 'sending…' : 'ask the owner for a top-up'}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  disabled={billingRequest === 'sending'}
                  onClick={() => { void sendBillingRequest(orgBilling.accountId, 'plan_change'); }}
                >
                  ask about a plan change
                </SecondaryButton>
                {billingRequest === 'failed' && (
                  <span style={{ fontSize: '0.82rem', color: '#A34E4E' }}>
                    couldn&rsquo;t reach the owner just now — try again in a moment.
                  </span>
                )}
              </>
            )}
          </div>
        </Section>
      </PageContainer>
    );
  }
  // Task #80: canManage members FALL THROUGH to the full owner surface below
  // (org-targeted writes). This early return is the READ-ONLY member view.
  if (orgBilling?.mode === 'view' && !orgBilling.canManage) {
    // ── The FULL workspace subscription read (owner directive 2026-08-17,
    // Report C: "Justin can't see the full subscription … although he
    // should"). A member holding billing.view/billing.manage sees the same
    // read detail the owner sees for the ACTIVE workspace — plan, seats,
    // term, renewal, trial state, and the usage-derived recommendation —
    // rendered from getWorkspaceSubscriptionStatus's facts payload. WRITES
    // stay exactly as gated before: checkout / plan changes remain on the
    // owner's own billing surfaces, so no CTA on this surface ever charges
    // or changes anything. Older functions deployments (no facts payload)
    // degrade to the coarse plan line instead of a dead end.
    const wsSub = orgBilling.sub;
    const wsLabel = orgBilling.orgLabel;
    const wsRate = rateCtx?.state.rateUsd;
    const wsFacts: WorkspaceTrialFacts = {
      subscriptionDocExists: wsSub != null,
      subscriptionPlan: wsSub?.plan ?? null,
      subscriptionIsTrial: wsSub?.isTrial === true,
      subscriptionIsClientFree: wsSub?.isClientFree === true,
      trialActive: orgBilling.trial ? orgBilling.trial.active : null,
      trialTier: orgBilling.trial?.tier ?? null,
      trialCreditCents: orgBilling.trial?.creditAmountCents ?? null,
      trialExpiresAtMs: orgBilling.trial?.expiresAtMs ?? wsSub?.trialExpiresAtMs ?? null,
    };
    const wsLiveCents =
      orgBilling.wallet?.olvBalance != null && typeof wsRate === 'number' && isFinite(wsRate) && wsRate > 0
        ? orgBilling.wallet.olvBalance * wsRate * 100
        : null;
    const wsTrial = deriveWorkspaceTrialStatus(wsFacts, wsLiveCents, Date.now());
    const wsTone: 'active' | 'soon' | 'ended' = wsTrial.isExpired
      ? 'ended'
      : wsTrial.isExpiringSoon
        ? 'soon'
        : 'active';
    const wsSpentUsd =
      orgBilling.wallet?.totalOlvSpent != null && typeof wsRate === 'number' && isFinite(wsRate) && wsRate > 0
        ? rateCtx!.olvToUsd(orgBilling.wallet.totalOlvSpent)
        : 0;
    const wsWuDelivered = usdToWu(wsSpentUsd);
    const wsCreditRemainingUsd =
      wsTrial.isTrialUser && typeof wsTrial.creditRemainingCents === 'number' && wsLiveCents !== null
        ? wsTrial.creditRemainingCents / 100
        : null;
    const wsHasPaidPlan = Boolean(
      wsSub &&
      ['active', 'trialing', 'past_due'].includes(wsSub.status || '') &&
      !wsTrial.isTrialUser && wsSub.plan !== 'trial'
    );
    const wsPlanDisplay = wsSub?.plan ? PLAN_DISPLAY[wsSub.plan] ?? null : null;
    const wsSeats = typeof wsSub?.seatCount === 'number' && wsSub.seatCount > 0 ? wsSub.seatCount : null;
    const wsStatusVariant: StatusBadgeProps['variant'] = !wsSub
      ? 'inactive'
      : wsSub.status === 'active'
        ? (wsSub.isTrial ? 'trialing' : 'active')
        : wsSub.status === 'trialing'
          ? 'trialing'
          : wsSub.status === 'past_due'
            ? 'past_due'
            : wsSub.status === 'canceled'
              ? 'canceled'
              : 'inactive';
    const wsStatusLabel = !wsSub
      ? 'No Plan'
      : wsSub.status === 'active'
        ? (wsSub.isTrial ? 'Trial' : 'Active')
        : wsSub.status === 'trialing'
          ? 'Trial'
          : wsSub.status === 'past_due'
            ? 'Past Due'
            : wsSub.status === 'canceled'
              ? 'Canceled'
              : (wsSub.status || 'unknown');
    const wsPriceLine = (() => {
      const amt = wsSub?.planAmountUsd;
      if (typeof amt !== 'number' || amt <= 0) return '';
      const seatsSuffix = wsSeats && wsSeats > 1 ? ` × ${wsSeats} Prest0ns` : '';
      if (wsSub?.planInterval === 'year') {
        const monthlyEq = Math.round(amt / 12);
        return `$${monthlyEq.toLocaleString()} / mo per Prest0n${seatsSuffix} · prepaid year — `;
      }
      if (wsSub?.plan?.startsWith('wu_annual')) {
        return `$${amt.toLocaleString()} / mo per Prest0n${seatsSuffix} · billed monthly, 12-month hire — `;
      }
      return `$${amt} / month — `;
    })();
    // The same usage-derived recommendation the owner sees, from the org size
    // on file — read-only here (the hire itself stays with the owner).
    const wsRec = recommendPlan((orgBilling.orgSizeBand as OrgSizeBand) || null);
    const wsRecSel = selectionPricing('annual', wsRec.seats);
    const wsOffersRevealed =
      !wsHasPaidPlan &&
      shouldRevealPlanOffers({
        isTrial: wsTrial.isTrialUser,
        totalSpentUsd: wsSpentUsd,
        trialEnded: wsTrial.isExpired,
      });
    // Three-way (task #78): an OWNER must never be told to "ask the owner" —
    // this branch only sees owners for HIRED created (co_) orgs, whose plan
    // doc is org-keyed and whose change flows haven't shipped yet.
    // Task #80: owners and billing.manage members never reach this branch —
    // they fall through to the full manage surface. This is view-only copy.
    const wsGatedNote = 'you can view this organization’s subscription; changes need the billing permission.';
    const wsSeatLimitLine = orgBilling.seatLimit
      ? (orgBilling.seatLimit.maxSeats === null
        ? 'unlimited seats (subscribed)'
        : `up to ${orgBilling.seatLimit.maxSeats} total seats`)
      : null;

    return (
      <>
      <PageContainer>
        <PageTitle>subscription</PageTitle>

        {/* ── The workspace's trial, stated up front — same band the owner
            sees, with the WORKSPACE's numbers (paying-wallet credit, owner
            trial term). ── */}
        {wsTrial.isTrialUser && (
          <TrialBand $tone={wsTone} data-org-billing-view="">
            <TrialBandEyebrow $tone={wsTone}>
              {wsTone === 'ended'
                ? `${wsLabel}’s trial has ended`
                : <>{wsLabel}’s plan · <KeepCase>Prest0n</KeepCase> — trial</>}
            </TrialBandEyebrow>
            <TrialBandTitle>
              {wsTone === 'ended'
                ? `${wsLabel}’s trial has ended — the workspace is safe`
                : wsTrial.daysRemaining != null
                  ? `${wsLabel}’s trial is running — ${wsTrial.daysRemaining} day${wsTrial.daysRemaining === 1 ? '' : 's'} left`
                  : `${wsLabel}’s trial is running`}
            </TrialBandTitle>
            <TrialBandLead>
              {wsTone === 'ended' ? (
                <>
                  chat is paused — the workspace and everything Prest0n has learned are safe.
                  hiring picks up right where it left off.
                </>
              ) : (
                <>
                  Prest0n is working on trial credit — no card, nothing charged
                  {wsTrial.endsOn ? <> through <strong>{wsTrial.endsOn}</strong></> : null}.
                  these are the same numbers the wallet page shows for this workspace.
                </>
              )}
            </TrialBandLead>

            <TrialStrip data-org-trial-strip="">
              {wsTrial.daysRemaining != null && wsTone !== 'ended' && (
                <TrialStat>
                  <TrialStatLabel>time left</TrialStatLabel>
                  <TrialStatValue>
                    {wsTrial.daysRemaining} day{wsTrial.daysRemaining === 1 ? '' : 's'}
                  </TrialStatValue>
                  {wsTrial.endsOn && <TrialStatSub>through {wsTrial.endsOn}</TrialStatSub>}
                </TrialStat>
              )}
              {wsCreditRemainingUsd != null && (
                <TrialStat>
                  <TrialStatLabel>trial credit left</TrialStatLabel>
                  <TrialStatValue>
                    {formatWu(usdToWu(wsCreditRemainingUsd))} <Pr0Unit />
                  </TrialStatValue>
                  <TrialStatSub>the workspace&rsquo;s wallet balance</TrialStatSub>
                </TrialStat>
              )}
              <TrialStat>
                <TrialStatLabel>work delivered</TrialStatLabel>
                <TrialStatValue>
                  {formatWu(wsWuDelivered)} <Pr0Unit />
                </TrialStatValue>
                <TrialStatSub>finished work, at the standard rate</TrialStatSub>
              </TrialStat>
            </TrialStrip>
          </TrialBand>
        )}

        {/* ── The workspace's plan — full read detail. ── */}
        {!wsTrial.isTrialUser && (
        <Section data-org-billing-view="">
          <SectionLabel>{wsLabel}&rsquo;s plan</SectionLabel>
          {wsSub ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <PlanName>{wsPlanDisplay?.name || wsSub.plan || 'unknown'}</PlanName>
                <StatusBadge variant={wsStatusVariant}>
                  <StatusDot variant={wsStatusVariant} />
                  {wsStatusLabel}
                </StatusBadge>
                {wsSub.isTest && <TestBadge>Test Mode</TestBadge>}
              </div>
              {(wsPriceLine || wsPlanDisplay?.tagline) && (
                <PlanPrice>{wsPriceLine}{wsPlanDisplay?.tagline || ''}</PlanPrice>
              )}
              {typeof wsSub.currentPeriodEnd === 'number' && wsSub.currentPeriodEnd > 0 && (
                <DetailRow>
                  <DetailLabel>
                    {wsSub.planInterval === 'year' ? 'renews automatically on' : 'next billing cycle'}
                  </DetailLabel>
                  <span>{formatDate(wsSub.currentPeriodEnd)}</span>
                </DetailRow>
              )}
              {wsSeats != null && (
                <DetailRow>
                  <DetailLabel>team size</DetailLabel>
                  <span>
                    {wsSeats} Prest0n{wsSeats > 1 ? 's' : ''}
                    {wsSub.plan?.startsWith('wu_')
                      ? ` · ${(wsSeats * (wsSub.planWuPerSeatMonthly ?? MONTHLY_WU_PER_PRESTON)).toLocaleString()} ${WU_LABEL} / month`
                      : ''}
                  </span>
                </DetailRow>
              )}
              {typeof wsSub.aiEnabled === 'boolean' && (
                <DetailRow>
                  <DetailLabel>ai tools</DetailLabel>
                  <span>{wsSub.aiEnabled ? 'enabled' : 'suspended'}</span>
                </DetailRow>
              )}
              {wsSub.status === 'past_due' && (
                <GraceNotice>
                  ⚠ the workspace&rsquo;s last payment failed. ai access continues during the
                  14-day grace period.
                  {wsSub.graceExpiresAt ? (
                    <> grace period ends <strong>{formatDate(wsSub.graceExpiresAt)}</strong>.</>
                  ) : null}
                </GraceNotice>
              )}
            </>
          ) : (
            <>
              <PlanName style={{ color: '#AAA', fontWeight: 400 }}>no active plan yet</PlanName>
              <PlanPrice>the workspace&rsquo;s plan appears here once the owner sets one up.</PlanPrice>
            </>
          )}
        </Section>
        )}

        {/* ── The usage-derived recommendation the owner sees — READ-ONLY:
            no checkout, no plan change; the hire stays with the owner. ── */}
        {wsOffersRevealed && (
          <RecCard>
            <RecEyebrow>
              {orgBilling.orgSizeBand && wsRec.minimumRequired
                ? `recommended for ${wsLabel}`
                : 'the hire, recommended'}
            </RecEyebrow>
            <RecReason>
              {wsTrial.isTrialUser && wsWuDelivered > 0 && (
                <>this workspace&rsquo;s trial has delivered{' '}
                <strong>{formatWu(wsWuDelivered)} {WU_LABEL}</strong> of finished work.{' '}</>
              )}
              {wsRec.reason}
            </RecReason>
            <RecHeadline>
              {wsRec.seats} Prest0n professional{wsRec.seats > 1 ? 's' : ''} · annual
            </RecHeadline>
            <RecPriceRow>
              <RecPerSeat>${wsRecSel.monthlyUsdPerSeat.toLocaleString()} / mo per Prest0n</RecPerSeat>
              <GoldPill>save {wsRecSel.savePct}%</GoldPill>
            </RecPriceRow>
            <MathStrip>
              {(wsRec.seats * wsRecSel.wuPerSeat).toLocaleString()} {WU_LABEL} every month
              {' · '}${(wsRec.seats * wsRecSel.monthlyUsdPerSeat).toLocaleString()} / mo, billed
              monthly — a 12-month hire, not an upfront payment
            </MathStrip>
            <RenewalLine>{wsGatedNote}</RenewalLine>
          </RecCard>
        )}

        {/* ── Seats + who can change things. ── */}
        <Section>
          <SectionLabel>seats &amp; changes</SectionLabel>
          {wsSeatLimitLine && (
            <DetailRow>
              <DetailLabel>seat allowance</DetailLabel>
              <span>{wsSeatLimitLine}</span>
            </DetailRow>
          )}
          <AdminOnlyNote style={{ margin: '0.75rem 0 0' }}>{wsGatedNote}</AdminOnlyNote>
          {/* Task #77: view access without change access still gets a real
              path forward — one click routes the request to the owner.
              Task #78: never rendered to the owner themself. */}
          {!orgBilling.isOwner && (
          <div style={{ marginTop: '0.9rem', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {billingRequest === 'sent' ? (
              <span style={{ fontSize: '0.85rem', color: '#4B7B5D' }} data-testid="billing-request-sent">
                sent — the owner&rsquo;s been notified.
              </span>
            ) : (
              <>
                {wsSub ? (
                  <>
                    <SecondaryButton
                      type="button"
                      data-testid="billing-request-topup"
                      disabled={billingRequest === 'sending'}
                      onClick={() => { void sendBillingRequest(orgBilling.accountId, 'top_up'); }}
                    >
                      {billingRequest === 'sending' ? 'sending…' : 'ask the owner for a top-up'}
                    </SecondaryButton>
                    <SecondaryButton
                      type="button"
                      disabled={billingRequest === 'sending'}
                      onClick={() => { void sendBillingRequest(orgBilling.accountId, 'plan_change'); }}
                    >
                      ask about a plan change
                    </SecondaryButton>
                  </>
                ) : (
                  <SecondaryButton
                    type="button"
                    data-testid="billing-request-hire"
                    disabled={billingRequest === 'sending'}
                    onClick={() => { void sendBillingRequest(orgBilling.accountId, 'hire'); }}
                  >
                    {billingRequest === 'sending' ? 'sending…' : 'ask the owner to hire Prest0n'}
                  </SecondaryButton>
                )}
                {billingRequest === 'failed' && (
                  <span style={{ fontSize: '0.82rem', color: '#A34E4E' }}>
                    couldn&rsquo;t reach the owner just now — try again in a moment.
                  </span>
                )}
              </>
            )}
          </div>
          )}
        </Section>
      </PageContainer>
      <GlobalFooter />
      </>
    );
  }

  if (checkoutPending) {
    return (
      <PageContainer>
        <PageTitle>subscription</PageTitle>
        <Section>
          <SetupOverlay>
            <Spinner />
            <SetupTitle>setting up your workspace…</SetupTitle>
            <SetupSub>confirming your subscription with Stripe. this takes a moment.</SetupSub>
          </SetupOverlay>
        </Section>
      </PageContainer>
    );
  }

  const commitmentFaq = (
    <Section>
      {/* Open by default (owner directive 2026-08-15): what you are committing
          to should be readable without a click — collapsing it made the terms
          feel like fine print. The summary still toggles it shut. */}
      <details open>
      <FaqSummary>what your commitment means — in plain language</FaqSummary>
      <FAQList>
        <FAQItem>
          <FAQQuestion>what exactly am I committing to?</FAQQuestion>
          <FAQAnswer>
            a number of Prest0ns for your term — a team size, not a usage bill. annual means
            those Prest0ns, at that rate, for twelve months. each Prest0n's{' '}
            {MONTHLY_WU_PER_PRESTON.toLocaleString()} monthly {WU_LABEL} are yours at any pace
            within their window: each month's allocation is good for that month and the next,
            then expires — like payroll, you staff for the workload you have.
          </FAQAnswer>
        </FAQItem>
        <FAQItem>
          <FAQQuestion>can we add Prest0ns mid-term?</FAQQuestion>
          <FAQAnswer>
            anytime, in one click. new hires are prorated and end together with your existing
            term — and every Prest0n's rate falls automatically as the team grows.
          </FAQAnswer>
        </FAQItem>
        <FAQItem>
          <FAQQuestion>can we reduce the number of Prest0ns?</FAQQuestion>
          <FAQAnswer>
            on a committed term — annual or the prepaid year — from the next term: one click,
            anytime before renewal. the commitment is what the discount pays for. on a monthly
            term, anytime — changes take effect at your next monthly cycle.
          </FAQAnswer>
        </FAQItem>
        <FAQItem>
          <FAQQuestion>what happens at renewal?</FAQQuestion>
          <FAQAnswer>
            your subscription renews automatically at your same team size unless you change it —
            one click, anytime before your renewal date. no notice periods, no phone calls. your
            rate is locked for the length of each term you're in — it never changes mid-term.
          </FAQAnswer>
        </FAQItem>
        <FAQItem>
          <FAQQuestion>can our bill surprise us?</FAQQuestion>
          <FAQAnswer>
            no. your committed team is a fixed number, and anything beyond it — overtime,
            top-ups, sprint blocks — exists only when an administrator authorizes it in advance.
            there is no such thing as an automatic overage charge at prest0.
          </FAQAnswer>
        </FAQItem>
      </FAQList>
      </details>
    </Section>
  );

  return (
    <>
      <PageContainer>
      <SeoHead
        title="Subscription | prest0"
        description="Manage your prest0 subscription, billing, and plan details."
        noIndex={true}
      />
      <PageTitle>subscription</PageTitle>

      {/* Org-targeted hire band (task #75): landed here straight from a new
          organization's onboarding — every hire on this visit is FOR that
          organization, and the moment it completes Prest0n starts working
          there. */}
      {targetOrgAccountId && (
        <TrialBand $tone="active">
          <TrialBandEyebrow $tone="active">hiring for your new organization</TrialBandEyebrow>
          <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
            your new organization is set up and saved — it just needs its Prest0n. pick the
            hire below and Prest0n starts working there immediately, with everything you
            chose during setup already in place.
          </div>
        </TrialBand>
      )}

      {/* Task #80 — org-manage banner: a billing.manage member (or owner)
          operating the ORGANIZATION's billing. Money routing stated plainly. */}
      {orgManage && orgBilling?.mode === 'view' && (
        <TrialBand $tone="active" data-org-billing-manage>
          <TrialBandEyebrow $tone="active">managing {orgBilling.orgLabel}&rsquo;s billing</TrialBandEyebrow>
          <div style={{ fontSize: '0.9rem', lineHeight: 1.6 }}>
            plan changes and hires bill the organization&rsquo;s card on file;
            top-ups charge your own card and credit the organization&rsquo;s wallet.
          </div>
        </TrialBand>
      )}

      {/* ── Trial, stated up front ──────────────────────────────────────────
          Leads the page whenever this is a trial account, so the first thing
          read is "you're on a trial and here's what that means" rather than
          the plan card's "No active plan". Driven by the live Firestore trial
          listener, so it is right even when the status call is still in
          flight or has failed. */}
      {!orgManage && isTrialAccount && !hasPaidPlan && (
        <TrialBand $tone={trialTone}>
          {/* 2026-08-16 owner directive: during a trial this band IS the plan
              section — the separate "your plan · Prest0n — trial" card was
              redundant and is merged here. */}
          <TrialBandEyebrow $tone={trialTone}>
            {trialTone === 'ended' ? 'your trial has ended' : <>your plan · <KeepCase>Prest0n</KeepCase> — trial</>}
          </TrialBandEyebrow>
          <TrialBandTitle>
            {trialTone === 'ended'
              ? 'your trial has ended — your workspace is safe'
              : trialStatus.daysRemaining != null
                ? `your trial is running — ${trialStatus.daysRemaining} day${trialStatus.daysRemaining === 1 ? '' : 's'} left`
                : 'your trial is running'}
          </TrialBandTitle>
          <TrialBandLead>
            {trialTone === 'ended' ? (
              <>
                chat is paused — your workspace and everything Prest0n has learned are safe.
                hire below to pick up right where you left off.
              </>
            ) : (
              <>
                Prest0n is working on trial credit — no card, nothing charged
                {trialStatus.endsOn ? <> through <strong>{trialStatus.endsOn}</strong></> : null}.
                hiring below is what comes next; nothing to decide today.
              </>
            )}
          </TrialBandLead>

          <TrialStrip>
            {trialStatus.daysRemaining != null && trialTone !== 'ended' && (
              <TrialStat>
                <TrialStatLabel>time left</TrialStatLabel>
                <TrialStatValue>
                  {trialStatus.daysRemaining} day{trialStatus.daysRemaining === 1 ? '' : 's'}
                </TrialStatValue>
                {trialStatus.endsOn && <TrialStatSub>through {trialStatus.endsOn}</TrialStatSub>}
              </TrialStat>
            )}
            {trialCreditRemainingUsd != null && (
              <TrialStat>
                <TrialStatLabel>trial credit left</TrialStatLabel>
                <TrialStatValue>
                  {/* ONE number everywhere (task #76): print the workspace's
                      actual wallet pr0 — identical to the wallet page. */}
                  {trialStatus.liveWalletWu !== null
                    ? formatWu(trialStatus.liveWalletWu)
                    : formatWu(usdToWu(trialCreditRemainingUsd))} <Pr0Unit />
                </TrialStatValue>
                <TrialStatSub>the workspace's wallet balance</TrialStatSub>
              </TrialStat>
            )}
            <TrialStat>
              <TrialStatLabel>work delivered</TrialStatLabel>
              <TrialStatValue>
                {formatWu(trialWuUsed)} <Pr0Unit />
              </TrialStatValue>
              <TrialStatSub>finished work, at the standard rate</TrialStatSub>
            </TrialStat>
          </TrialStrip>

          <TrialBandNote>
            {trialTone === 'ended' ? (
              <>
                everything Prest0n learned carries straight into the full account.
              </>
            ) : (
              <>
                <strong>at the end:</strong> chat pauses — your workspace and everything
                Prest0n has learned stay safe. hiring keeps the same Prest0n working without
                interruption.
              </>
            )}
          </TrialBandNote>
        </TrialBand>
      )}

      {/* ── Your Plan — always first, always personal. During a trial the
          band above IS the plan section (2026-08-16 owner: merged — the
          separate trial plan card was redundant). ── */}
      {!isTrialAccount && (
      <Section>
        <SectionLabel>your plan</SectionLabel>

        {loading ? (
          <LoadingText>loading…</LoadingText>
        ) : hasActiveSubscription ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <PlanName>{planDisplay?.name || 'starter'}</PlanName>
              <StatusBadge variant={statusVariant()}>
                <StatusDot variant={statusVariant()} />
                {statusLabel()}
              </StatusBadge>
              {subscription?.isTest && <TestBadge>Test Mode</TestBadge>}
            </div>
            <PlanPrice>
              {activePriceLine}{planDisplay?.tagline || 'core ai tools'}
            </PlanPrice>

            <ValueNotice>
              <ValueTitle>what this covers</ValueTitle>
              <ValueText>
                {subscription?.plan?.startsWith('wu_')
                  ? `each prest0n professional is a full-time digital employee in your firm's private secure workspace — ${((subscription?.planWuPerSeatMonthly ?? MONTHLY_WU_PER_PRESTON)).toLocaleString()} ${WU_FULL_NAME} (pr0) of finished work every month per Prest0n, itemized on your monthly work statement.`
                  : subscription?.plan === 'enterprise'
                    ? 'enterprise gives your team a dedicated ai environment with isolated compute, advanced security controls, custom integrations, and white-glove onboarding.'
                    : subscription?.plan === 'professional'
                      ? 'professional gives your team priority compute, Slack integration, advanced workflows, and multi-user access on top of the core private ai workspace.'
                      : 'this plan gives your team a dedicated ai employee with a private secure workspace, isolated infrastructure, and always-on access to research, drafting, translation, and workflow automation.'}
              </ValueText>
            </ValueNotice>

            <DetailRow>
              <DetailLabel>
                {subscription?.planInterval === 'year' ? 'renews automatically on' : 'next billing cycle'}
              </DetailLabel>
              <span>{formatDate(subscription!.currentPeriodEnd)}</span>
            </DetailRow>
            {typeof subscription?.planSeats === 'number' && subscription.planSeats > 0 && subscription?.plan?.startsWith('wu_') && (
              <DetailRow>
                <DetailLabel>team size</DetailLabel>
                <span>
                  {subscription.planSeats} Prest0n{subscription.planSeats > 1 ? 's' : ''} ·{' '}
                  {(subscription.planSeats * (subscription.planWuPerSeatMonthly ?? MONTHLY_WU_PER_PRESTON)).toLocaleString()} {WU_LABEL} / month
                </span>
              </DetailRow>
            )}
            <DetailRow>
              <DetailLabel>ai tools</DetailLabel>
              <span>{subscription!.aiEnabled ? 'enabled' : 'suspended'}</span>
            </DetailRow>

            {subscription!.status === 'past_due' && (
              <GraceNotice>
                ⚠ your last payment failed. ai access continues during the 14-day grace period.
                {subscription!.graceExpiresAt && (
                  <> grace period ends <strong>{formatDate(subscription!.graceExpiresAt)}</strong>.</>
                )}
                {' '}update your payment method below.
              </GraceNotice>
            )}

            {/* (2026-08-14 copy diet: the renewal-explainer notice that used
                to sit here duplicated the RenewalLine + FAQ word for word —
                removed.) */}

            {!orgManage ? (
              <SecondaryButton onClick={handleManage} disabled={actionLoading}>
                {actionLoading ? 'opening…' : 'manage subscription'}
              </SecondaryButton>
            ) : (
              <AdminOnlyNote>
                payment methods and invoices stay on the owner&rsquo;s billing
                portal — ask the owner for card changes.
              </AdminOnlyNote>
            )}

            {/* ── One-click plan adjuster (2026-08-14): every change — hire
                more, fire some, switch cadence — is stepper + confirm,
                charged to the card on file. Checkout is only the fallback
                when no live payment method exists. ── */}
            {hasPaidPlan && subscription?.plan?.startsWith('wu_') && canManageHere && (
              <AdjustBox data-adjust-panel>
                <AdjustHead>adjust your team — one click, card on file</AdjustHead>
                <TermToggle role="tablist" aria-label="billing term">
                  <TermButton type="button" role="tab" aria-selected={adjTerm === 'annual'} $active={adjTerm === 'annual'} onClick={() => { adjTouched.current = true; setAdjTerm('annual'); }}>
                    annual — billed monthly
                  </TermButton>
                  <TermButton type="button" role="tab" aria-selected={adjTerm === 'monthly'} $active={adjTerm === 'monthly'} onClick={() => { adjTouched.current = true; setAdjTerm('monthly'); }}>
                    monthly
                  </TermButton>
                  <TermButton type="button" role="tab" aria-selected={adjTerm === 'prepay'} $active={adjTerm === 'prepay'} onClick={() => { adjTouched.current = true; setAdjTerm('prepay'); }}>
                    prepay the year — {WU_PRICING.prepayDiscountPct}% off
                  </TermButton>
                </TermToggle>
                <StepperRow>
                  <StepBtn
                    type="button"
                    aria-label="Remove a Prest0n"
                    onClick={() => { adjTouched.current = true; setAdjSeats((s) => Math.max(1, s - 1)); }}
                    disabled={adjSeats <= 1}
                  >
                    −
                  </StepBtn>
                  <SeatCount>{adjSeats}</SeatCount>
                  <StepBtn
                    type="button"
                    aria-label="Add a Prest0n"
                    onClick={() => { adjTouched.current = true; setAdjSeats((s) => Math.min(MAX_SEATS, s + 1)); }}
                    disabled={adjSeats >= MAX_SEATS}
                  >
                    +
                  </StepBtn>
                  <StepperLabel>
                    Prest0n{adjSeats > 1 ? 's' : ''} · currently {subscription?.planSeats ?? 1}
                  </StepperLabel>
                </StepperRow>
                {(() => {
                  const adjSel = selectionPricing(adjTerm, adjSeats);
                  return (
                    <AdjustPreviewLine>
                      {adjSeats} × ${adjSel.monthlyUsdPerSeat.toLocaleString()} ={' '}
                      <strong>${(adjSeats * adjSel.monthlyUsdPerSeat).toLocaleString()} / mo</strong>
                      {adjTerm === 'prepay' && adjSel.upfrontTotalUsd != null && (
                        <> (${adjSel.upfrontTotalUsd.toLocaleString()} once for the year — {WU_PRICING.prepayDiscountPct}% off)</>
                      )}
                      {' · '}{(adjSeats * adjSel.wuPerSeat).toLocaleString()} {WU_LABEL} every month
                      <br />
                      {!adjDirty
                        ? 'this is your current plan — step the team up or down, or switch the term.'
                        : adjPreview?.loading
                          ? 'checking what changes…'
                          : adjPreview?.isUpgrade
                            ? (typeof adjPreview.amountDueNowCents === 'number' && adjPreview.amountDueNowCents >= 0
                                ? `applies immediately — about $${(adjPreview.amountDueNowCents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} prorated to ${cardOnFileLabel} today.`
                                : `applies immediately — the prorated difference goes to ${cardOnFileLabel}.`)
                            : `nothing charged today — this takes effect when your commitment is up${adjPreview?.periodEnd ? `, ${formatDate(adjPreview.periodEnd)}` : ''}.`}
                    </AdjustPreviewLine>
                  );
                })()}
                <RecCta
                  onClick={applyPlanChange}
                  disabled={!adjDirty || adjApplying || adjPreview?.loading === true}
                  style={{ marginTop: 0 }}
                >
                  {adjApplying ? 'applying…' : !adjDirty ? 'no change selected' : 'apply this change'}
                </RecCta>
                {adjResult && <AdjustSuccess>{adjResult}</AdjustSuccess>}
              </AdjustBox>
            )}

            {/* ── Upgrade / change paths for paid WU subscribers ── */}
            {hasPaidPlan && subscription?.plan?.startsWith('wu_') && canManageHere && (
              <ChangeGrid>
                {subscription.plan === 'wu_monthly' ? (
                  <ChangeCard>
                    <ChangeTitle>move to annual — save {WU_PRICING.annual.savePct}% or more</ChangeTitle>
                    <ChangeText>
                      same Prest0n{(subscription.planSeats ?? 1) > 1 ? 's' : ''}, same work —
                      ${(((WU_PRICING.monthly.monthlyUsd - WU_PRICING.annual.monthlyUsd) * 12) * (subscription.planSeats ?? 1)).toLocaleString()}{' '}
                      back over the year{(subscription.planSeats ?? 1) > 1 ? ' across your team' : ''}.
                      commitment is the only discount we have.
                    </ChangeText>
                    <ChangeAction
                      type="button"
                      onClick={() => {
                        // Preset the one-click adjuster — no checkout.
                        adjTouched.current = true;
                        setAdjTerm('annual');
                        setAdjSeats(subscription.planSeats ?? 1);
                        document.querySelector('[data-adjust-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      disabled={actionLoading}
                    >
                      commit annual at my team size →
                    </ChangeAction>
                  </ChangeCard>
                ) : currentTierRank > 0 && currentTierRank < 3 ? (
                  <ChangeCard>
                    <ChangeTitle>add Prest0ns — the rate falls automatically</ChangeTitle>
                    <ChangeText>
                      expanding is one click, prorated, and ends together with your term — every
                      Prest0n's rate drops as the team grows.
                    </ChangeText>
                    <ChangeAction
                      type="button"
                      onClick={() => {
                        const next = Math.max((subscription.planSeats ?? 1) + 1, currentTierRank === 1 ? 3 : 5);
                        adjTouched.current = true;
                        setAdjSeats(next);
                        document.querySelector('[data-adjust-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      disabled={actionLoading}
                    >
                      expand my team →
                    </ChangeAction>
                  </ChangeCard>
                ) : (
                  <ChangeCard>
                    <ChangeTitle>you're on the best rate</ChangeTitle>
                    <ChangeText>
                      expanding your team is still one click, prorated, co-terminous — and
                      prepaying the year takes {WU_PRICING.prepayDiscountPct}% off across the team.
                    </ChangeText>
                    <ChangeAction
                      type="button"
                      onClick={() => {
                        adjTouched.current = true;
                        setAdjSeats((subscription.planSeats ?? 5) + 1);
                        document.querySelector('[data-adjust-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      disabled={actionLoading}
                    >
                      add another Prest0n →
                    </ChangeAction>
                  </ChangeCard>
                )}

                <ChangeCard>
                  <ChangeTitle>need to scale down?</ChangeTitle>
                  <ChangeText>
                    {subscription.plan?.startsWith('wu_prepay')
                      ? 'your prepaid year is committed — reduce from the next year, one click anytime before it renews.'
                      : subscription.planCommitmentMonths
                        ? 'your hire is committed for twelve months — reduce from the next term, one click anytime before renewal. that commitment is what the discount pays for.'
                        : 'on a monthly term, adjust your team size anytime — changes take effect at your next monthly cycle.'}
                  </ChangeText>
                  <ChangeAction
                    type="button"
                    onClick={() => {
                      // Firing Prest0ns is the same one-click adjuster, just
                      // stepping DOWN — scheduled at renewal, never a portal
                      // maze. (Cancel + payment methods stay in Manage above.)
                      adjTouched.current = true;
                      setAdjSeats(Math.max(1, (subscription.planSeats ?? 1) - 1));
                      document.querySelector('[data-adjust-panel]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    disabled={actionLoading}
                  >
                    reduce my team →
                  </ChangeAction>
                </ChangeCard>
              </ChangeGrid>
            )}
            {hasPaidPlan && subscription?.plan?.startsWith('wu_') && !orgManage && usageVisibility.ready && !usageVisibility.canManageBilling && (
              <AdminOnlyNote style={{ marginTop: '1rem', marginBottom: 0 }}>
                plan changes are managed by your workspace administrator.
              </AdminOnlyNote>
            )}
          </>
        ) : (
          <>
            {/* Trial accounts never reach this section (the band above IS
                their plan section) — this is genuinely "nothing running". */}
            <PlanName style={{ color: '#AAA', fontWeight: 400 }}>no active plan</PlanName>
            <PlanPrice>
              {offersRevealed
                ? 'make the hire below — one click.'
                : 'your plan options appear here.'}
            </PlanPrice>
            {isTestMode && !orgManage && (
              <>
                <div style={{ fontSize: '0.75rem', color: '#856404', background: '#FFF3CD', padding: '0.5rem 0.75rem', borderRadius: '6px', marginTop: '0.75rem', lineHeight: 1.5 }}>
                  Testing mode is active. For plan-specific test subscriptions, use the pricing page flow so the selected tier is preserved into checkout.
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  <SecondaryButton onClick={() => handleSubscribe('starter', undefined, true)} disabled={actionLoading}>Test Starter</SecondaryButton>
                  <SecondaryButton onClick={() => handleSubscribe('professional', undefined, true)} disabled={actionLoading}>Test Professional</SecondaryButton>
                  <SecondaryButton onClick={() => handleSubscribe('enterprise', undefined, true)} disabled={actionLoading}>Test Enterprise</SecondaryButton>
                </div>
              </>
            )}
          </>
        )}

        {/* Only alarm the user when the page genuinely has nothing to show.
            (Trial accounts never render this section at all — their live
            band already carries the real state.) */}
        {error && <ErrorText>{error}</ErrorText>}
      </Section>
      )}

      {/* ── Trial, quiet: no prices until $25 of pr0 has actually flowed ── */}
      {!loading && isTrialAccount && !offersRevealed && (
        <Section>
          <SectionLabel>your next step</SectionLabel>
          <ValueText style={{ fontSize: '0.9rem' }}>
            keep putting Prest0n to work. once{' '}
            <strong>{formatWu(usdToWu(TRIAL_PRICING_REVEAL_USD))} {WU_LABEL}</strong> of real work
            has flowed through your trial, your hiring options unlock right here, sized to your
            organization.
          </ValueText>
        </Section>
      )}

      {/* ── The offer — annual first, explorable, personal ── */}
      {showOfferCard && (
        <RecCard>
          <RecEyebrow>
            {orgBand && rec.minimumRequired
              ? 'recommended for your organization'
              : 'the hire, recommended'}
          </RecEyebrow>
          <RecReason>
            {/* Task #70 copy diet: one line of proof, one line of offer —
                never an org-size justification. */}
            {isTrialAccount && trialWuUsed > 0 && (
              <>your trial has delivered <strong>{formatWu(trialWuUsed)} {WU_LABEL}</strong> of
              finished work.{' '}</>
            )}
            {rec.reason}
          </RecReason>

          {/* Inline org-size capture when nothing is on file — the
              recommendation personalizes the moment it's answered. */}
          {orgBandLoaded && !orgBand && (
            <>
              <StepperLabel>how many people work at your organization?</StepperLabel>
              <OrgChipRow>
                {ORG_SIZE_BANDS.map((b) => (
                  <OrgChip
                    key={b.value}
                    type="button"
                    $active={false}
                    onClick={() => handleSelectOrgBand(b.value)}
                  >
                    {b.label}
                  </OrgChip>
                ))}
              </OrgChipRow>
            </>
          )}

          {/* 2026-08-16 owner: the cadence choices — prepay's 20% off above
              all — stay VISIBLE, never behind a collapse. Default stays the
              annual hire; the toggle is the whole customization surface. */}
          <TermToggle role="tablist" aria-label="billing term" style={{ margin: '0.5rem 0 0.6rem' }}>
            <TermButton type="button" role="tab" aria-selected={term === 'annual'} $active={term === 'annual'} onClick={() => setTerm('annual')}>
              annual — billed monthly
            </TermButton>
            <TermButton type="button" role="tab" aria-selected={term === 'monthly'} $active={term === 'monthly'} onClick={() => setTerm('monthly')}>
              monthly
            </TermButton>
            <TermButton type="button" role="tab" aria-selected={term === 'prepay'} $active={term === 'prepay'} onClick={() => setTerm('prepay')}>
              prepay the year — {WU_PRICING.prepayDiscountPct}% off
            </TermButton>
          </TermToggle>

          <RecHeadline>
            {recSeats} Prest0n professional{recSeats > 1 ? 's' : ''} ·{' '}
            {term === 'prepay' ? 'prepaid year' : term}
          </RecHeadline>
          <RecPriceRow>
            <RecPerSeat>${sel.monthlyUsdPerSeat.toLocaleString()} / mo per Prest0n</RecPerSeat>
            <GoldPill>save {sel.savePct}%</GoldPill>
            {teamRateApplied && <TeamRatePill>team rate applied</TeamRatePill>}
            {term === 'monthly' && <GoldPill>cancel anytime</GoldPill>}
            {term === 'prepay' && <GoldPill>an extra {WU_PRICING.prepayDiscountPct}% off for paying upfront</GoldPill>}
          </RecPriceRow>

          <MathStrip>
            {totalWu.toLocaleString()} {WU_LABEL} every month
            {term === 'annual' && (
              <>{' · '}${totalMonthly.toLocaleString()} / mo, billed monthly — a 12-month hire, not an upfront payment</>
            )}
            {term === 'monthly' && <>{' · '}${totalMonthly.toLocaleString()} / mo</>}
            {term === 'prepay' && (
              <>{' · '}${prepayTotal.toLocaleString()} once for the year — {WU_PRICING.prepayDiscountPct}% off is what paying upfront earns</>
            )}
          </MathStrip>

          <StepperRow>
            <StepBtn
              type="button"
              aria-label="Remove a Prest0n"
              onClick={() => setRecSeats((s) => Math.max(minSeats, s - 1))}
              disabled={recSeats <= minSeats}
            >
              −
            </StepBtn>
            <SeatInput
              type="number"
              min={minSeats}
              max={MAX_SEATS}
              value={recSeats}
              aria-label="Number of Prest0ns"
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isFinite(v)) setRecSeats(v);
              }}
              onBlur={() => setRecSeats((s2) => Math.min(MAX_SEATS, Math.max(minSeats, s2 || minSeats)))}
            />
            <StepBtn
              type="button"
              aria-label="Add a Prest0n"
              onClick={() => setRecSeats((s) => Math.min(MAX_SEATS, s + 1))}
              disabled={recSeats >= MAX_SEATS}
            >
              +
            </StepBtn>
            <StepperLabel>Prest0n{recSeats > 1 ? 's' : ''} — adjust your deployment</StepperLabel>
          </StepperRow>
          {recSeats === rec.seats && rec.seats > rec.minSeats && (
            <StepHint>
              our recommendation for your team — step down to {rec.minSeats} anytime
            </StepHint>
          )}
          {recSeats === rec.minSeats && rec.minSeats > 1 && (
            <StepHint>
              the floor for the {rec.minSeats}+ team rate
            </StepHint>
          )}

          <RecCta
            onClick={() => handleSubscribe(sel.tier, recSeats)}
            disabled={actionLoading}
          >
            {actionLoading
              ? 'opening…'
              : term === 'annual'
                ? 'make the hire — pay monthly'
                : term === 'prepay'
                  ? `prepay the year — ${WU_PRICING.prepayDiscountPct}% off, one payment`
                  : 'start monthly — checkout'}
          </RecCta>

          <RenewalLine>
            renews automatically at your team size · rate locked for your{' '}
            {term === 'monthly' ? 'monthly' : 'twelve-month'} term · change team size in one click
            before renewal · nothing beyond your committed team ever bills without your
            administrator's approval
          </RenewalLine>

          {/* Task #70: the price ladder here repeated the headline, the math
              strip, and the pricing page's rate card three ways — the term
              toggle + seat stepper already recalculate every rate. What was
              MISSING was what a pr0 buys: the same published work-rate card
              the pricing page shows, so the buyer sees the work, not just
              the price. */}
          <StepHint style={{ marginTop: '1.1rem' }}>
            flat rates, published for everyone — overruns, retries, and thinking depth are
            always 0 {WU_LABEL}.
          </StepHint>
          {/* 2026-08-19 owner directive: the launcher graduates from a text
              link to the shiny gold pill — same modal, just impossible to
              miss. Centered on its own row; the value line under it is the
              page's ONE value-prop sentence (copy-diet doctrine holds). */}
          <CalcCtaRow>
            <WorkCalculatorLauncher variant="bold" label={`see what your ${WU_LABEL} buy`} />
          </CalcCtaRow>
          <StepHint style={{ marginTop: '0.55rem', textAlign: 'center' }}>
            thousands of built-in tools and skills from day one — and every
            model-side improvement lands in your plan automatically.
          </StepHint>

          {/* 2026-08-16 owner directive: the "see the full published rate
              card" button is gone — the card above already shows the rates. */}
          <QuietRow>
            <QuietAction type="button" onClick={() => navigate('/wallet?action=add-tokens')}>
              top up {WU_LABEL} instead
            </QuietAction>
          </QuietRow>
        </RecCard>
      )}
      {showAdminOnlyNote && (
        <AdminOnlyNote>plan changes are managed by your workspace administrator.</AdminOnlyNote>
      )}

      {/* ── Commitment expectations — collapsed by default (2026-08-14 copy
          diet: the five answers stay one click away instead of a wall of
          prose on the page). ── */}
      {(showOfferCard || (hasPaidPlan && subscription?.plan?.startsWith('wu_'))) && commitmentFaq}

      {/* ── The payment popup (task #73): OUR branded hire confirm — pays
          like the top-up modal. Saved card preselected, one click; a
          CardElement appears only when the buyer chooses a new card. The
          Stripe-hosted embedded checkout (Link-first, many fields) is gone. ── */}
      <Modal
        isOpen={hireSel !== null}
        onClose={() => setHireSel(null)}
        title="complete your hire"
        maxWidth="560px"
        footer={null}
      >
        {hireSel && (
          <Elements
            stripe={getSharedStripePromise()}
            options={{ appearance: { variables: { fontFamily: 'inherit' } } }}
          >
            <HireConfirmBody
              tier={hireSel.tier}
              seats={hireSel.seats}
              accountId={billingTarget?.accountId ?? null}
              orgLabel={billingTarget?.label ?? null}
              onSuccess={handleHireSuccess}
            />
          </Elements>
        )}
      </Modal>

      {/* ── PRO Work Units balance pointer ── */}
      <Section>
        <SectionLabel>PRO Work Units</SectionLabel>
        <div style={{ fontSize: '0.875rem', color: '#666', lineHeight: 1.6 }}>
          work is measured in PRO Work Units (pr0) and deducted from your wallet — on-demand,
          overtime, and top-ups convert at the published standard rate of ${STANDARD_RATE_USD_PER_WU} per pr0.{' '}
          <a
            href="/wallet"
            style={{ color: 'var(--brand-text)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
          >
            view wallet balance →
          </a>
        </div>
      </Section>
      </PageContainer>
      {/* Every other page closes with the shared footer; this one was the
          outlier (owner report 2026-08-15). Outside PageContainer so it spans
          the full width instead of the 900px content column. */}
      <GlobalFooter />
    </>
  );
};

export default Subscription;
